<script setup lang="ts">
import { ref, reactive, computed, watch, watchEffect, nextTick, onMounted, onBeforeUnmount } from 'vue';
import api from '~/api';
import AssetPickerDialog from '~/components/seedance/AssetPickerDialog.vue';
import PromptFromImageDialog from '~/components/seedance/PromptFromImageDialog.vue';
import { useSnackbarStore } from '~/composables/useSnackBarStore';
import { TYPE_DURATION_LIMIT, sumDurations } from '~/utils/seedanceMedia';
import { estimateSeedanceCredits } from '~/utils/seedancePricing';
import {
  type MentionAsset,
  mentionToken,
  normalizeMentions,
  splitByMentions,
} from '~/utils/seedanceMentions';
import rawTemplates from './seedance-templates.json';

const snackbar = useSnackbarStore();

const props = defineProps<{ sourceImage: string; isUploading?: boolean }>();
const emit = defineEmits<{
  (e: 'result', payload: { url: string; type: 'video' }): void;
  (e: 'state', s: { canGenerate: boolean; label: string; costLabel: string; busy: boolean }): void;
  // 非阻塞提交完成 —— 通知右侧 Your generations 刷新出「生成中」卡片
  (e: 'submitted'): void;
}>();
const { t } = useI18n();

// -------- Global state (referenced by many sections) --------
const errorMsg = ref('');
// 面板根元素 —— 传给素材选择弹窗做 PC 侧板定位锚点
const panelRef = ref<HTMLElement | null>(null);

// -------- Model --------
type Model =
  | 'seedance_2_0'
  | 'seedance_2_0_fast'
  | 'seedance_2_0_mini'
  | 'seedance_2_5'
  | 'minimax_h3';
// Seedance 2.5 not officially launched yet. Default to the cheapest tier
// (Mini — 720p / 5s) so new users land on the lowest-credit option.
const model = ref<Model>('seedance_2_0_mini');

// 卡片触发器 + 下拉面板：2.0 默认可用，Fast/Mini/2.5 全部 Coming Soon
const modelOpen = ref(false);
const MODEL_OPTIONS: Array<{
  value: Model;
  label: string;
  /** 列表里展示的一行短文案 */
  shortDescription: string;
  /** hover 浮层里展示的完整文案 */
  description: string;
  comingSoon?: boolean;
  /** 标签徽章（如 NEW / PRO）—— new = 强调色胶囊，pro = 琥珀金胶囊 */
  badge?: { text: string; variant: 'new' | 'pro' };
}> = [
  {
    value: 'seedance_2_5',
    label: 'Seedance 2.5',
    comingSoon: true,
    shortDescription: t('seedance.create.modelSeedance25Short'),
    description: t('seedance.create.modelSeedance25Desc'),
  },
  {
    value: 'seedance_2_0',
    label: 'Seedance 2.0',
    badge: { text: 'PRO', variant: 'pro' },
    shortDescription: t('seedance.create.modelSeedance20Short'),
    description: t('seedance.create.modelSeedance20Desc'),
  },
  {
    value: 'seedance_2_0_fast',
    label: 'Seedance 2.0 Fast',
    shortDescription: t('seedance.create.modelFastShort'),
    description: t('seedance.create.modelFastDesc'),
  },
  {
    value: 'seedance_2_0_mini',
    label: 'Seedance 2.0 Mini',
    badge: { text: 'NEW', variant: 'new' },
    shortDescription: t('seedance.create.modelMiniShort'),
    description: t('seedance.create.modelMiniDesc'),
  },
  {
    value: 'minimax_h3',
    label: 'MiniMax H3',
    badge: { text: 'NEW', variant: 'new' },
    shortDescription: t('seedance.create.modelMinimaxH3Short'),
    description: t('seedance.create.modelMinimaxH3Desc'),
  },
];
const currentModelOption = computed(
  // 兜底按 value 找，不能用下标 —— 往数组里插项会让默认选中项错位
  () =>
    MODEL_OPTIONS.find((o) => o.value === model.value) ??
    MODEL_OPTIONS.find((o) => o.value === 'seedance_2_0')!,
);
function pickModel(opt: (typeof MODEL_OPTIONS)[number]) {
  if (opt.comingSoon) return;
  model.value = opt.value;
  modelOpen.value = false;
}

// -------- Mode: Image / Frame --------
// image 模式不传素材即纯文生视频，无独立 text 模式
type Mode = 'image' | 'frame';
const mode = ref<Mode>('image');

// 官方示例模板（Example 按钮 / 结果流第一张卡片）
const EXAMPLE_TEMPLATE =
  ((rawTemplates as any).image ?? []).find((t: any) => t.id === 'sample-image-6') ?? null;

// -------- Reference assets (image mode) --------
// Kinovi API 上限：9 图 + 3 视频 + 3 音频（视频/音频各自时长总和 ≤15s，由 API 校验）
const REF_LIMITS = { image: 9, video: 3, audio: 3 } as const;

function refKind(format: string): keyof typeof REF_LIMITS {
  if (format === 'video') return 'video';
  if (format === 'audio') return 'audio';
  return 'image';
}

const refImages = ref<string[]>([]);   // up to 9 images
const refVideos = ref<string[]>([]);   // up to 3 videos
const refAudios = ref<string[]>([]);   // up to 3 audios
const hasRefs = computed(() =>
  refImages.value.length > 0 || refVideos.value.length > 0 || refAudios.value.length > 0,
);

// When upload zone gives a new image, auto-fill reference in image mode
// or start frame in frame mode. (text mode has no reference assets)
watch(() => props.sourceImage, (url) => {
  if (!url) return;
  if (mode.value === 'image') {
    if (!refImages.value.includes(url)) {
      refImages.value = [url, ...refImages.value].slice(0, REF_LIMITS.image);
    }
  } else if (mode.value === 'frame') {
    if (!startFrame.value) startFrame.value = url;
  }
});

// -------- Frame slots --------
const startFrame = ref('');
const endFrame = ref('');

// -------- Pickers --------
const showAssetPicker = ref(false);
const assetPickerType = ref<'image' | 'video' | 'audio' | 'media' | 'all'>('image');
const assetPickerTarget = ref<'refImages' | 'startFrame' | 'endFrame'>('refImages');
const assetPickerMulti = ref(false);

function openImagePicker(target: typeof assetPickerTarget.value, multi = false) {
  assetPickerType.value = 'image';
  assetPickerTarget.value = target;
  assetPickerMulti.value = multi;
  showAssetPicker.value = true;
}
// 统一 Reference Assets 选择：image + video + audio 混合（type=all 含音频）
function openMediaPicker() {
  assetPickerType.value = 'all';
  assetPickerTarget.value = 'refImages';
  assetPickerMulti.value = true;
  showAssetPicker.value = true;
}
async function onAssetPicked(items: Array<{ url: string; format?: string | null }>) {
  const target = assetPickerTarget.value;
  const urls = items.map((i) => i.url);
  if (target === 'refImages') {
    // 统一入口：按 format 分派到 refImages / refVideos / refAudios。
    // 数量超上限（9/3/3）跳过；视频/音频再校验各自总时长 ≤ TYPE_DURATION_LIMIT（后端硬限，
    // 从 Library 选已上传素材也必须查，否则会出现总时长超限提交被后端拒的情况）。
    const imgs = [...refImages.value];
    const vids = [...refVideos.value];
    const auds = [...refAudios.value];
    let blockedCount = false;
    let blockedDurVideo = false;
    let blockedDurAudio = false;
    let usedVideo = await sumDurations(vids, 'video');
    let usedAudio = await sumDurations(auds, 'audio');
    for (const it of items) {
      const kind = refKind(it.format ?? '');
      if (kind === 'image') {
        if (imgs.includes(it.url)) continue;
        if (imgs.length >= REF_LIMITS.image) { blockedCount = true; continue; }
        imgs.push(it.url);
        continue;
      }
      const bucket = kind === 'video' ? vids : auds;
      if (bucket.includes(it.url)) continue;
      if (bucket.length >= REF_LIMITS[kind]) { blockedCount = true; continue; }
      const dur = (await sumDurations([it.url], kind)) || 0;
      const used = kind === 'video' ? usedVideo : usedAudio;
      if (used + dur > TYPE_DURATION_LIMIT + 0.3) {
        if (kind === 'video') blockedDurVideo = true; else blockedDurAudio = true;
        continue;
      }
      bucket.push(it.url);
      if (kind === 'video') usedVideo += dur; else usedAudio += dur;
    }
    refImages.value = imgs;
    refVideos.value = vids;
    refAudios.value = auds;
    if (blockedDurVideo)
      snackbar.showError(t('seedance.assetPicker.toastDurationBudgetVideo', { seconds: TYPE_DURATION_LIMIT }));
    else if (blockedDurAudio)
      snackbar.showError(t('seedance.assetPicker.toastDurationBudgetAudio', { seconds: TYPE_DURATION_LIMIT }));
    else if (blockedCount) snackbar.showError(t('seedance.create.toastRefLimit'));
  } else if (target === 'startFrame') startFrame.value = urls[0] ?? '';
  else if (target === 'endFrame') endFrame.value = urls[0] ?? '';
}
function removeRefImage(i: number) { refImages.value = refImages.value.filter((_, idx) => idx !== i); }
function removeRefVideo(i: number) { refVideos.value = refVideos.value.filter((_, idx) => idx !== i); }
function removeRefAudio(i: number) { refAudios.value = refAudios.value.filter((_, idx) => idx !== i); }

// -------- 共享 AssetPickerDialog 适配 --------
// 共享组件的 type 仅 all|image|video|audio；本面板的 'media' 归并到 'all'
const pickerSharedType = computed<'all' | 'image' | 'video' | 'audio'>(() =>
  assetPickerType.value === 'media' ? 'all' : (assetPickerType.value as any),
);
// 直接添加模式仅用于 Reference Assets（refImages 目标）；帧图选择保持单选
const isRefTarget = computed(() => assetPickerTarget.value === 'refImages');
const addedRefUrls = computed(() =>
  isRefTarget.value ? [...refImages.value, ...refVideos.value, ...refAudios.value] : undefined,
);
// 弹窗内上传视频/音频的 15s 时长预算校验用（混合数组含 format）
const referenceAssetsForBudget = computed(() => [
  ...refImages.value.map((url) => ({ url, format: 'image' })),
  ...refVideos.value.map((url) => ({ url, format: 'video' })),
  ...refAudios.value.map((url) => ({ url, format: 'audio' })),
]);
// 直接添加模式下点击已添加素材 → 按 url 从对应桶移除
function onRefRemoveByUrl(url: string) {
  refImages.value = refImages.value.filter((u) => u !== url);
  refVideos.value = refVideos.value.filter((u) => u !== url);
  refAudios.value = refAudios.value.filter((u) => u !== url);
}

// 直接上传入口已全部移除 —— 素材/帧图统一走 Library 选择面板（面板内可上传）

// -------- Prompt --------
const prompt = ref('');

// 从图片生成提示词：结果空则填、有内容则换行追加
const promptFromImageOpen = ref(false);
const onPromptFromImage = (generated: string) => {
  const cur = prompt.value.trimEnd();
  prompt.value = (cur ? `${cur}\n${generated}` : generated).slice(0, 10000);
};
const fullscreenOpen = ref(false);

// -------- Prompt length warning（只在超过 2500 时提示，上限 10000） --------
const promptTooLong = computed(() => prompt.value.length > 2500);

/* ---------- @mention chip 编辑器 ----------
 * 提示词输入框是 contenteditable：@image1 等 token 渲染成
 * 「缩略图 + token + 底色」的原子 chip（contentEditable=false，退格整体删除）。
 * prompt 字符串仍是唯一数据源，chip 的 data-mention 保存 token 文本。
 */

const CHIP_ICON_SVG: Record<string, string> = {
  audio:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>',
  image:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>',
  video:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m22 8-6 4 6 4V8Z"/><rect width="14" height="12" x="2" y="6" rx="2" ry="2"/></svg>',
};

function buildChipEl(token: string, asset: MentionAsset | undefined): HTMLSpanElement {
  const chip = document.createElement('span');
  chip.contentEditable = 'false';
  chip.dataset.mention = token;
  chip.className = 'm-chip';

  // 左侧小缩略图 + hover 时浮出的 X（点击删除该引用，编辑器 onClick 委托处理）
  const thumbWrap = document.createElement('span');
  thumbWrap.className = 'm-chip-thumbwrap';
  let thumb: HTMLElement;
  if (asset?.format === 'video') {
    const v = document.createElement('video');
    v.src = asset.url;
    v.preload = 'metadata';
    v.muted = true;
    v.playsInline = true;
    v.className = 'm-chip-thumb';
    thumb = v;
  } else if (asset?.format === 'image') {
    const img = document.createElement('img');
    img.src = asset.url;
    img.alt = '';
    img.className = 'm-chip-thumb';
    thumb = img;
  } else {
    // 音频没有画面 / 素材已被删除 —— 用类型图标兜底
    const icon = document.createElement('span');
    icon.className = 'm-chip-icon';
    const fmt = token.startsWith('@audio') ? 'audio' : token.startsWith('@video') ? 'video' : 'image';
    icon.innerHTML = CHIP_ICON_SVG[fmt];
    thumb = icon;
  }
  const removeBtn = document.createElement('span');
  removeBtn.dataset.chipRemove = 'true';
  removeBtn.title = t('seedance.create.removeReference');
  removeBtn.className = 'm-chip-remove';
  removeBtn.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';
  thumbWrap.append(thumb, removeBtn);
  chip.appendChild(thumbWrap);

  const label = document.createElement('span');
  label.textContent = token;
  label.title = t('seedance.create.switchAsset');
  label.className = 'm-chip-label';
  chip.appendChild(label);

  // hover 时在 chip 上方浮出固定方形预览（不随素材比例变化）
  const preview = document.createElement('span');
  preview.className = 'm-chip-preview';
  if (asset?.format === 'video') {
    const pv = document.createElement('video');
    pv.src = asset.url;
    pv.preload = 'metadata';
    pv.muted = true;
    pv.loop = true;
    pv.playsInline = true;
    preview.appendChild(pv);
    // 只在 hover 期间播放，避免隐藏状态常驻消耗
    chip.addEventListener('mouseenter', () => {
      pv.play().catch(() => {});
    });
    chip.addEventListener('mouseleave', () => pv.pause());
  } else if (asset?.format === 'image') {
    const pi = document.createElement('img');
    pi.src = asset.url;
    pi.alt = '';
    preview.appendChild(pi);
  } else {
    // 音频/缺失素材：方形底 + 大图标
    const bigIcon = document.createElement('span');
    bigIcon.className = 'm-chip-preview-icon';
    bigIcon.innerHTML = CHIP_ICON_SVG[token.startsWith('@audio') ? 'audio' : 'image'];
    preview.appendChild(bigIcon);
  }
  chip.appendChild(preview);
  return chip;
}

/** prompt 字符串 → 编辑器 DOM（文本节点 + chip） */
function buildEditorContent(el: HTMLElement, text: string, assets: Map<string, MentionAsset>): void {
  el.textContent = '';
  for (const seg of splitByMentions(text)) {
    if (seg.type === 'text') {
      el.appendChild(document.createTextNode(seg.value));
    } else {
      el.appendChild(buildChipEl(seg.token, assets.get(seg.token)));
    }
  }
}

/** 编辑器 DOM → prompt 字符串（chip → token；div/br → 换行） */
function serializeEditor(root: Node): string {
  let out = '';
  root.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      out += child.nodeValue ?? '';
      return;
    }
    if (!(child instanceof HTMLElement)) return;
    if (child.dataset.mention) {
      out += child.dataset.mention;
    } else if (child.tagName === 'BR') {
      out += '\n';
    } else {
      // 块级元素（浏览器回车产生的 div）—— 前面补换行，内部单 br 视为空行
      if (out !== '') out += '\n';
      const inner = serializeEditor(child);
      out += inner === '\n' ? '' : inner;
    }
  });
  return out;
}

function placeCaretAtEnd(el: HTMLElement): void {
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}

// token → 素材（chip 缩略图 & mention 弹层展示用）—— 从本面板的
// refImages / refVideos / refAudios 三个数组构建同类型序号 token。
const mentionAssets = computed<Map<string, MentionAsset>>(() => {
  const map = new Map<string, MentionAsset>();
  refImages.value.forEach((url, i) => map.set(mentionToken('image', i + 1), { url, format: 'image' }));
  refVideos.value.forEach((url, i) => map.set(mentionToken('video', i + 1), { url, format: 'video' }));
  refAudios.value.forEach((url, i) => map.set(mentionToken('audio', i + 1), { url, format: 'audio' }));
  return map;
});

// @ 引用只在 image（reference 素材）模式下有意义
const mentionOptions = computed<Array<{ token: string; asset: MentionAsset }>>(() => {
  if (mode.value !== 'image') return [];
  return Array.from(mentionAssets.value, ([token, asset]) => ({ token, asset }));
});

/* ---------- chip 编辑器实例工厂（主输入框 / 全屏弹窗各一份） ----------
 * 两个编辑器共享 prompt ref 作为唯一数据源：任一编辑器输入把自己的
 * lastSynced 标成最新值，另一个编辑器的 watch 发现不一致后重建 DOM，
 * 实现双向同步且各自光标不跳动。
 */
function createChipEditor(opts: {
  /** 编辑器挂载（v-if 出现）时自动聚焦并把光标放到末尾 */
  autoFocus?: boolean;
  /** 弹层翻转计算的可视边界（滚动容器）；不传用视口 */
  getClipEl?: () => HTMLElement | null;
} = {}) {
  const el = ref<HTMLDivElement>();
  const wrapEl = ref<HTMLDivElement>();
  const listEl = ref<HTMLUListElement>();
  const showMentions = ref(false);
  // 弹层中当前高亮项（鼠标 hover / 上下键共用）
  const activeIdx = ref(0);
  const mentionPos = ref<{ top: number; left: number }>({ top: 0, left: 0 });
  // 锚点下方空间不够时翻转到上方，避免弹层被底部裁掉
  const mentionPlacement = ref<'below' | 'above'>('below');
  // 用户输入路径写入的最新 prompt —— 与 ref 相同则跳过 DOM 重建（避免光标跳动）
  let lastSynced: string | null = null;
  let lastAssetsSig = '';
  let lastEl: HTMLDivElement | null = null;
  // 非 null = chip 替换模式：记录被点击 chip 在编辑器里的序号
  let replaceIndex: number | null = null;

  // 模板函数 ref（v-if 卸载时收到 null）
  const setEl = (node: unknown) => { el.value = (node as HTMLDivElement | null) ?? undefined; };
  const setWrapEl = (node: unknown) => { wrapEl.value = (node as HTMLDivElement | null) ?? undefined; };
  const setListEl = (node: unknown) => { listEl.value = (node as HTMLUListElement | null) ?? undefined; };

  const closeMentions = () => {
    showMentions.value = false;
    replaceIndex = null;
  };

  // prompt 被外部写入（模板/清空/另一处编辑器/Use）或素材变化时重建编辑器内容
  const syncFromState = () => {
    const editor = el.value;
    if (!editor) return;
    // 外部写入可能带旧格式 [Image 1] —— 先归一化成 @image1 再渲染（会再触发本 watch）
    const norm = normalizeMentions(prompt.value);
    if (norm !== prompt.value) {
      prompt.value = norm;
      return;
    }
    const assetsSig = Array.from(mentionAssets.value, ([k, v]) => `${k}:${v.url}`).join('|');
    if (editor === lastEl && prompt.value === lastSynced && assetsSig === lastAssetsSig) return;
    buildEditorContent(editor, prompt.value, mentionAssets.value);
    // 编辑器刚挂载（全屏弹窗打开）—— 聚焦并把光标放到末尾
    if (editor !== lastEl && opts.autoFocus) {
      editor.focus();
      placeCaretAtEnd(editor);
    }
    lastEl = editor;
    lastSynced = prompt.value;
    lastAssetsSig = assetsSig;
  };
  watch([prompt, mentionAssets, el], syncFromState, { flush: 'post' });
  onMounted(syncFromState);

  // 编辑器 DOM → prompt ref
  const syncFromDom = () => {
    const editor = el.value;
    if (!editor) return;
    // contenteditable 会产生 &nbsp;，统一还原成普通空格
    let text = serializeEditor(editor).replace(/ /g, ' ');
    // 全删后浏览器可能残留 <br> 序列化成换行 —— 视为真空
    if (!/\S/.test(text)) text = '';
    const clipped = text.slice(0, 10000);
    lastSynced = clipped;
    prompt.value = clipped;
    if (clipped !== text) {
      // 超长被截断 —— 重建一次，光标回到末尾
      buildEditorContent(editor, clipped, mentionAssets.value);
      placeCaretAtEnd(editor);
    }
  };

  // 光标前一个字符是 @ 就触发弹层 —— 不要求前面必须有空格，
  // 方便用户直接粘着单词写 "beach@image1"
  const detectMentionAtCaret = (): boolean => {
    const editor = el.value;
    const sel = window.getSelection();
    const node = sel?.anchorNode;
    if (!editor || !sel || !node || !editor.contains(node)) return false;
    if (node.nodeType !== Node.TEXT_NODE || sel.anchorOffset === 0) return false;
    return (node.nodeValue ?? '').charAt(sel.anchorOffset - 1) === '@';
  };

  // 把弹层定位到给定矩形下方；下方可视空间不足且上方更宽裕时翻到上方
  // （渲染时 .above 用 translateY(-100%) 贴齐，实际高度自适应）
  const positionPopoverForRect = (rect: { left: number; top: number; bottom: number }) => {
    const wrap = wrapEl.value;
    if (!wrap) return;
    const wrapRect = wrap.getBoundingClientRect();
    // 防止弹层超出右边界被外层容器裁掉
    const POPOVER_W = 256; // 16rem
    const left = Math.max(0, Math.min(rect.left - wrapRect.left, wrap.clientWidth - POPOVER_W));
    // 可视边界 = 视口 ∩ 滚动容器（主输入框在 overflow 面板里，弹层逃不出面板裁切）
    const clip = opts.getClipEl?.() ?? null;
    let boundTop = 0;
    let boundBottom = window.innerHeight;
    if (clip) {
      const clipRect = clip.getBoundingClientRect();
      boundTop = Math.max(boundTop, clipRect.top);
      boundBottom = Math.min(boundBottom, clipRect.bottom);
    }
    // 预估高度：标题区 ~40px + 每项 48px（列表最高 360px）
    const estimatedH = 40 + Math.min(mentionOptions.value.length * 48, 360);
    const spaceBelow = boundBottom - rect.bottom;
    const spaceAbove = rect.top - boundTop;
    if (spaceBelow < estimatedH + 12 && spaceAbove > spaceBelow) {
      mentionPlacement.value = 'above';
      mentionPos.value = { top: rect.top - wrapRect.top - 4, left };
    } else {
      mentionPlacement.value = 'below';
      mentionPos.value = { top: rect.bottom - wrapRect.top + 4, left };
    }
  };

  // 光标处触发（输入 @）时的定位
  const positionMentionPopover = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0).cloneRange();
    range.collapse(true);
    const rect = range.getClientRects()[0] ?? range.getBoundingClientRect();
    positionPopoverForRect(rect);
  };

  const onInput = () => {
    syncFromDom();
    if (detectMentionAtCaret() && mentionOptions.value.length > 0) {
      // 输入 @ 触发的是插入模式，不是 chip 替换
      replaceIndex = null;
      activeIdx.value = 0;
      positionMentionPopover();
      showMentions.value = true;
    } else {
      closeMentions();
    }
  };

  // 点击已有 chip —— 打开弹层切换该 chip 引用的素材；
  // 点击 chip 缩略图上 hover 浮出的 X —— 直接删除该引用
  const onClick = (e: MouseEvent) => {
    const editor = el.value;
    const target = e.target as HTMLElement;
    const removeBtn = target.closest?.('[data-chip-remove]') as HTMLElement | null;
    if (editor && removeBtn && editor.contains(removeBtn)) {
      removeBtn.closest('[data-mention]')?.remove();
      closeMentions();
      syncFromDom();
      return;
    }
    const chip = target.closest?.('[data-mention]') as HTMLElement | null;
    if (editor && chip && editor.contains(chip) && mentionOptions.value.length > 0) {
      const chips = Array.from(editor.querySelectorAll('[data-mention]'));
      replaceIndex = chips.indexOf(chip);
      const optIdx = mentionOptions.value.findIndex((o) => o.token === chip.dataset.mention);
      activeIdx.value = optIdx >= 0 ? optIdx : 0;
      // 光标停到 chip 后面并确保编辑器持有焦点（上下键导航需要）
      editor.focus();
      const sel = window.getSelection();
      if (sel) {
        const r = document.createRange();
        r.setStartAfter(chip);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
      }
      positionPopoverForRect(chip.getBoundingClientRect());
      showMentions.value = true;
    } else if (showMentions.value && !detectMentionAtCaret()) {
      // 点击编辑器其他位置 —— 收起弹层
      closeMentions();
    }
  };

  // 替换第 chipIdx 个 chip 的引用素材（chip 用序号定位，
  // 失焦重建后 DOM 节点会换新，但序号保持稳定）
  const replaceChipAt = (chipIdx: number, token: string) => {
    const editor = el.value;
    closeMentions();
    if (!editor) return;
    const targetChip = editor.querySelectorAll<HTMLElement>('[data-mention]')[chipIdx];
    if (!targetChip) return;
    const next = buildChipEl(token, mentionAssets.value.get(token));
    targetChip.replaceWith(next);
    editor.focus();
    const sel = window.getSelection();
    if (sel) {
      const r = document.createRange();
      r.setStartAfter(next);
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
    }
    syncFromDom();
  };

  // 选中弹层项：删除触发字符 @，在光标处插入 chip + 空格
  const insertMention = (token: string) => {
    const editor = el.value;
    if (!editor) return;
    editor.focus();
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editor.contains(sel.anchorNode)) {
      const node = sel.anchorNode;
      // 触发字符 "@" 一并删除
      if (
        node &&
        node.nodeType === Node.TEXT_NODE &&
        sel.anchorOffset > 0 &&
        (node.nodeValue ?? '').charAt(sel.anchorOffset - 1) === '@'
      ) {
        const start = sel.anchorOffset - 1;
        (node as Text).deleteData(start, 1);
        sel.collapse(node, start);
      }
      const range = sel.getRangeAt(0);
      range.collapse(true);
      const chip = buildChipEl(token, mentionAssets.value.get(token));
      const space = document.createTextNode(' ');
      range.insertNode(space);
      range.insertNode(chip);
      const after = document.createRange();
      after.setStartAfter(space);
      after.collapse(true);
      sel.removeAllRanges();
      sel.addRange(after);
    } else {
      // 兜底：追加到末尾
      editor.appendChild(buildChipEl(token, mentionAssets.value.get(token)));
      editor.appendChild(document.createTextNode(' '));
      placeCaretAtEnd(editor);
    }
    closeMentions();
    syncFromDom();
  };

  // 弹层选中某项：chip 替换模式 or 光标插入模式
  const chooseOption = (token: string) => {
    if (replaceIndex !== null) replaceChipAt(replaceIndex, token);
    else insertMention(token);
  };

  // @ 按钮 — 在光标处插入 @ 并（有素材时）弹出选择列表
  const insertAtSymbol = () => {
    const editor = el.value;
    if (!editor) return;
    editor.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !editor.contains(sel.anchorNode)) {
      placeCaretAtEnd(editor);
    }
    // execCommand 会触发 input 事件 → 走 onInput 的检测/弹层
    document.execCommand('insertText', false, '@');
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeMentions();
      return;
    }
    if (!showMentions.value || mentionOptions.value.length === 0) return;
    // 弹层打开时：上下键循环切换选中项，Enter 确认
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const delta = e.key === 'ArrowDown' ? 1 : mentionOptions.value.length - 1;
      activeIdx.value = (activeIdx.value + delta) % mentionOptions.value.length;
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = mentionOptions.value[activeIdx.value] ?? mentionOptions.value[0];
      chooseOption(opt.token);
    }
  };

  // 粘贴强制纯文本；带 token 时重建一次把它们渲染成 chip
  const onPaste = (e: ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData?.getData('text/plain') ?? '';
    document.execCommand('insertText', false, text);
    if (splitByMentions(text).some((s) => s.type === 'mention')) {
      requestAnimationFrame(() => {
        const editor = el.value;
        if (!editor) return;
        const raw = serializeEditor(editor).replace(/ /g, ' ');
        const norm = normalizeMentions(raw).slice(0, 10000);
        buildEditorContent(editor, norm, mentionAssets.value);
        lastSynced = norm;
        prompt.value = norm;
        placeCaretAtEnd(editor);
      });
    }
  };

  // 失焦时把手动敲出来的完整 token 也补渲染成 chip
  const onBlur = () => {
    const editor = el.value;
    if (!editor || lastSynced === null) return;
    buildEditorContent(editor, lastSynced, mentionAssets.value);
  };

  // 弹层打开期间：点击编辑区以外任何地方都收起
  const onDocMouseDown = (e: MouseEvent) => {
    const wrap = wrapEl.value;
    if (wrap && !wrap.contains(e.target as Node)) closeMentions();
  };
  watch(showMentions, (open) => {
    if (open) document.addEventListener('mousedown', onDocMouseDown);
    else document.removeEventListener('mousedown', onDocMouseDown);
  });
  onBeforeUnmount(() => document.removeEventListener('mousedown', onDocMouseDown));

  // 上下键切换时保证选中项滚动可见
  watch(activeIdx, () => {
    if (!showMentions.value) return;
    nextTick(() =>
      listEl.value?.querySelectorAll('li')[activeIdx.value]?.scrollIntoView({ block: 'nearest' }),
    );
  });

  return {
    setEl,
    setWrapEl,
    setListEl,
    showMentions,
    activeIdx,
    mentionPos,
    mentionPlacement,
    onInput,
    onClick,
    onKeyDown,
    onPaste,
    onBlur,
    insertAtSymbol,
    chooseOption,
  };
}

// 主输入框在可滚动面板里 —— 弹层翻转以面板可视区为边界
const mainEditor = reactive(createChipEditor({ getClipEl: () => panelRef.value ?? null }));
// 全屏弹窗编辑器：打开即聚焦、光标到末尾
const fsEditor = reactive(createChipEditor({ autoFocus: true }));

// -------- Params --------
// 9:21 后端仍兼容，但 UI 只展示 6 档
const aspect = ref<'16:9' | '9:16' | '1:1' | '4:3' | '3:4' | '21:9' | '9:21'>('3:4');
const ASPECTS: Array<typeof aspect.value> = ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16'];
const duration = ref(5);
// MiniMax H3 走自建 msg-center 后端，档位与 Seedance 两套都不同
const isH3 = computed(() => model.value === 'minimax_h3');
// 2.0 家族（含 fast/mini）用 2.0 参数（4-15s）；2.5 走另一档（4-30s）；H3 单独一套
const isV20 = computed(() => model.value !== 'seedance_2_5' && !isH3.value);
// fast/mini 仅 480p/720p
const isLite = computed(
  () => model.value === 'seedance_2_0_fast' || model.value === 'seedance_2_0_mini'
);
// H3 仅 7 档（上游按 17 帧倍数 snap）；2.0 支持 4-15s 全部整数秒；2.5 到 30s
const H3_DURATIONS = [4, 5, 6, 8, 10, 12, 15];
const durations = computed(() => isH3.value
  ? H3_DURATIONS
  : isV20.value
    ? [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15]
    : [4, 5, 6, 8, 10, 12, 15, 20, 30]);
const resolution = ref<'480p' | '720p' | '1080p' | '4k'>('720p');
// fast/mini 仅 480p/720p；H3 与 2.5 到 1080p；2.0 到 4K
const resolutions = computed(() => isH3.value
  ? ['480p', '720p', '1080p'] as const
  : isLite.value
    ? ['480p', '720p'] as const
    : isV20.value
      ? ['480p', '720p', '1080p', '4k'] as const
      : ['480p', '720p', '1080p'] as const);
const bitrate = ref<'standard' | 'high'>('standard');

// If model switch invalidates resolution, snap back to 720p
watch(model, () => {
  if (!(resolutions.value as readonly string[]).includes(resolution.value)) resolution.value = '720p';
  // 切到 H3 后若当前时长不在支持档位（如 7s），就近向上取
  if (!durations.value.includes(duration.value)) {
    duration.value = durations.value.find((d) => d > duration.value) ?? durations.value[durations.value.length - 1];
  }
});

// -------- Output number（批量生成数，1-4） --------
const batchCount = ref(1);

// -------- Ratio visualization --------
// 按真实宽高比缩放的示意框尺寸（长边 16px，短边最小 7px，装进 18×18 容器）
function ratioBox(id: string) {
  const [w, h] = id.split(':').map(Number);
  const scale = 16 / Math.max(w, h);
  return {
    width: `${Math.max(w * scale, 7)}px`,
    height: `${Math.max(h * scale, 7)}px`,
  };
}

// -------- Pricing (mirror server/utils/kinovi.ts) --------
// 计价只看是否有参考视频及其总时长（图片/音频不影响价格）。
// fast/mini 直传真实模型 —— 估算按提交模型算，保持展示 == 实收
// （fast/mini 价已在 seedancePricing 表中预置，超档在函数内钳到 720）。
const refVideoUrls = computed(() => (mode.value === 'image' ? [...refVideos.value] : []));
// 参考视频总时长探测（有缓存，通常瞬时返回）—— 未探测完按 15s 上限估
const refVideoSeconds = ref<number | undefined>(0);
watch(
  refVideoUrls,
  (urls) => {
    if (urls.length === 0) {
      refVideoSeconds.value = 0;
      return;
    }
    const snapshot = urls.join('\n');
    refVideoSeconds.value = undefined;
    void sumDurations(urls, 'video').then((s) => {
      if (refVideoUrls.value.join('\n') === snapshot) refVideoSeconds.value = s;
    });
  },
  { immediate: true }
);
const estimatedCredits = computed(() =>
  estimateSeedanceCredits({
    model: model.value,
    resolution: resolution.value,
    duration: duration.value,
    vidCount: refVideoUrls.value.length,
    refVideoSeconds: refVideoSeconds.value,
    batchCount: batchCount.value,
  })
);

// -------- Submit --------
const submitting = ref(false);
const polling = ref(false);
const progressLabel = ref('');

// prompt 未引用全部素材时的自定义确认弹窗（替代原生 window.confirm）
const missingRefDialog = ref<{ tokens: string } | null>(null);
let missingRefResolve: ((ok: boolean) => void) | null = null;
function confirmMissingRefs(tokens: string): Promise<boolean> {
  return new Promise((resolve) => {
    missingRefResolve = resolve;
    missingRefDialog.value = { tokens };
  });
}
function resolveMissingRef(ok: boolean) {
  missingRefDialog.value = null;
  missingRefResolve?.(ok);
  missingRefResolve = null;
}

const canGenerate = computed(() => {
  if (submitting.value || polling.value || props.isUploading) return false;
  if (mode.value === 'frame') return !!startFrame.value;
  // image 模式：prompt 和素材至少要有一个（不传素材即纯文生视频）
  return !!prompt.value.trim() || hasRefs.value;
});

function buildImageUrls(): string[] {
  if (mode.value === 'frame') {
    const arr = [startFrame.value];
    if (endFrame.value) arr.push(endFrame.value);
    return arr.filter(Boolean);
  }
  return [...refImages.value].filter(Boolean).slice(0, REF_LIMITS.image);
}

async function submitOne(): Promise<string> {
  const promptFinal = prompt.value.trim() || 'Cinematic video';
  const imageUrls = buildImageUrls();
  // text 模式没有 reference 素材
  const isImage = mode.value === 'image';
  const videoUrls = isImage && refVideos.value.length ? [...refVideos.value] : undefined;
  const audioUrls = isImage && refAudios.value.length ? [...refAudios.value] : undefined;
  // 参考视频总时长上报 —— 计费按官方口径（输入视频时长同样计 token）
  const referenceVideoSeconds = videoUrls ? await sumDurations(videoUrls, 'video') : undefined;
  const r = await api.seedanceCreate({
    // fast/mini 直传真实模型（后端映射到 Kinovi seedance2-fast / seedance2.0-mini）
    model: model.value,
    prompt: promptFinal,
    imageUrls: imageUrls.length ? imageUrls : undefined,
    videoUrls,
    audioUrls,
    referenceVideoSeconds,
    aspectRatio: aspect.value,
    duration: duration.value,
    resolution: resolution.value,
    // H3 走自建后端，无 bitrate 概念
    bitrate_mode: isH3.value ? undefined : bitrate.value,
    // frame 模式始终显式标 keyframe —— H3 靠它区分「首帧 i2v」与「参考图 r2v」
    mode: mode.value === 'frame' ? 'keyframe' : undefined,
  });
  return r.task_id;
}

async function generate() {
  if (props.isUploading) { errorMsg.value = t('editor.imageUploading'); return; }
  if (mode.value === 'frame' && !startFrame.value) { errorMsg.value = t('seedance.create.errStartFrameRequired'); return; }
  // image 模式：prompt 和素材至少要有一个（不传素材即纯文生视频）
  if (mode.value === 'image' && !prompt.value.trim() && !hasRefs.value) {
    errorMsg.value = t('seedance.create.disabledEnterPromptOrRef');
    return;
  }

  // Image 模式：检查 prompt 是否引用了全部素材；未引用则弹提示但允许继续。
  // 归一化后按规范 token（@image1 等）比对，与 chip 编辑器 / 后端一致。
  if (mode.value === 'image') {
    const normPrompt = normalizeMentions(prompt.value);
    const missing = Array.from(mentionAssets.value.keys()).filter(
      (token) => !normPrompt.includes(token),
    );
    if (missing.length > 0) {
      const ok = await confirmMissingRefs(missing.join(', '));
      if (!ok) return;
    }
  }

  errorMsg.value = '';
  submitting.value = true;
  try {
    for (let i = 0; i < batchCount.value; i++) {
      progressLabel.value = batchCount.value > 1 ? `Submitting ${i + 1}/${batchCount.value}…` : '';
      await submitOne();
    }
    // 非阻塞：提交完成即通知右侧 Your generations 刷新出「生成中」卡片，
    // 按钮立即恢复。进度/结果由结果流自身轮询翻新（cron 兜底长任务），
    // 不再在此阻塞式长轮询、也不切到整屏「Generating…」文案。
    emit('submitted');
    snackbar.showSuccess(t('seedance.create.generating'));
  } catch (e: any) {
    errorMsg.value = e?.message || t('seedance.common.submitFailed');
  } finally {
    submitting.value = false;
    polling.value = false;
    progressLabel.value = '';
  }
}

// 生成按钮文案（面板内 sticky 按钮与上抛 state 共用）
const footerLabel = computed(() =>
  submitting.value
    ? (progressLabel.value || t('seedance.create.generating'))
    : polling.value
      ? (progressLabel.value || t('seedance.create.generating'))
      : t('seedance.create.generate'),
);

watchEffect(() => {
  emit('state', {
    canGenerate: canGenerate.value,
    label: footerLabel.value,
    costLabel: t('seedance.create.creditsBadge', { count: estimatedCredits.value }),
    busy: submitting.value || polling.value,
    mode: mode.value,
  } as any);
});

// -------- Template gallery apply --------
// 归一化交给 ~/utils/seedanceMentions.normalizeMentions —— 旧格式 [Image 1]
// 与 @Image 1 等写法统一转成规范 token @image1（与 chip 编辑器一致）。

// 只写入 template 里已给的字段，未设置的字段保留用户当前输入。
// 空字符串 prompt 也跳过。
type TemplateAsset = { url: string; format?: string } | string;
type SeedanceTemplateInput = {
  id?: string;
  name?: string;
  model?: Model;
  prompt?: string;
  aspectRatio?: typeof aspect.value;
  duration?: number;
  resolution?: typeof resolution.value;
  bitrate?: 'standard' | 'high';
  batchCount?: number;
  referenceMedia?: TemplateAsset[];
  referenceAudio?: TemplateAsset[];
  startFrame?: TemplateAsset | null;
  endFrame?: TemplateAsset | null;
};
function urlOf(a: TemplateAsset | null | undefined): string {
  if (!a) return '';
  return typeof a === 'string' ? a : a.url;
}
function applyTemplate(tpl: SeedanceTemplateInput) {
  // 刻意不动模型 —— 模板只是 prompt/素材/输出参数的预设，
  // 用什么模型生成由用户自己决定，套预设不该把人踢到别的模型上。
  if (tpl.prompt !== undefined) prompt.value = normalizeMentions(tpl.prompt).slice(0, 10000);
  if (tpl.aspectRatio !== undefined) aspect.value = tpl.aspectRatio;
  if (tpl.duration !== undefined) duration.value = tpl.duration;
  if (tpl.resolution !== undefined) resolution.value = tpl.resolution;
  if (tpl.bitrate !== undefined) bitrate.value = tpl.bitrate;
  if (tpl.batchCount !== undefined) batchCount.value = tpl.batchCount;
  if (tpl.referenceMedia !== undefined) {
    // 按 format 拆分成 refImages / refVideos / refAudios（9/3/3 上限）
    const imgs: string[] = [];
    const vids: string[] = [];
    const auds: string[] = [];
    for (const item of tpl.referenceMedia) {
      const isString = typeof item === 'string';
      const url = isString ? item : item.url;
      const format = isString ? 'photo' : (item.format || 'photo');
      const kind = refKind(format);
      const bucket = kind === 'video' ? vids : kind === 'audio' ? auds : imgs;
      if (bucket.length < REF_LIMITS[kind] && !bucket.includes(url)) bucket.push(url);
    }
    refImages.value = imgs;
    refVideos.value = vids;
    refAudios.value = auds;
  }
  if (tpl.referenceAudio !== undefined) {
    refAudios.value = tpl.referenceAudio
      .map((a) => urlOf(a))
      .filter(Boolean)
      .slice(0, REF_LIMITS.audio);
  }
  if (tpl.startFrame !== undefined) startFrame.value = urlOf(tpl.startFrame);
  if (tpl.endFrame !== undefined) endFrame.value = urlOf(tpl.endFrame);
  // 不弹提示 —— prompt 与素材会立刻变化，页面本身就是反馈
}

// 用户再次点击已选中的模板时调用 —— 把所有可写字段重置为默认值。
function resetTemplate() {
  // 同样不碰模型：清空预设不等于要换模型
  mode.value = 'image';
  refImages.value = [];
  refVideos.value = [];
  refAudios.value = [];
  startFrame.value = '';
  endFrame.value = '';
  prompt.value = '';
  aspect.value = '16:9';
  duration.value = 5;
  resolution.value = '720p';
  bitrate.value = 'standard';
  batchCount.value = 1;
  snackbar.showSuccess(t('seedance.create.toastTemplateCleared'));
}

// -------- Example（一键应用 sample-image-6 完整示例，再点清空） --------
function applyExample() {
  if (!EXAMPLE_TEMPLATE) return;
  mode.value = 'image';
  applyTemplate(EXAMPLE_TEMPLATE as SeedanceTemplateInput);
}
const isExampleActive = computed(() => {
  const p = prompt.value.trim();
  // 应用后的 prompt 已归一化成 @image1 —— 模板原文（[Image 1]）也归一化后再比对
  return (
    !!EXAMPLE_TEMPLATE &&
    p !== '' &&
    p === normalizeMentions(String(EXAMPLE_TEMPLATE.prompt ?? '')).trim()
  );
});
function toggleExample() {
  if (isExampleActive.value) resetTemplate();
  else applyExample();
}

// -------- 结果流 Use：按订单 params 回填素材/prompt/输出设置并还原模式 --------
function applyFromOrder(video: any) {
  const p = (video?.params ?? {}) as Record<string, any>;
  const imageUrls: string[] = p.imageUrls ?? [];
  const videoUrls: string[] = p.videoUrls ?? [];
  const audioUrls: string[] = p.audioUrls ?? [];
  if (p.mode === 'keyframe') {
    mode.value = 'frame';
    startFrame.value = imageUrls[0] ?? '';
    endFrame.value = imageUrls[1] ?? '';
    refImages.value = [];
    refVideos.value = [];
    refAudios.value = [];
  } else {
    mode.value = 'image';
    refImages.value = [...imageUrls].slice(0, REF_LIMITS.image);
    refVideos.value = [...videoUrls].slice(0, REF_LIMITS.video);
    refAudios.value = [...audioUrls].slice(0, REF_LIMITS.audio);
    startFrame.value = '';
    endFrame.value = '';
  }
  if (p.prompt) prompt.value = String(p.prompt).slice(0, 10000);
  if (p.aspectRatio) aspect.value = p.aspectRatio as typeof aspect.value;
  if (p.resolution) resolution.value = p.resolution as typeof resolution.value;
  if (p.duration != null) {
    const d = Number(String(p.duration).replace(/s$/i, ''));
    if (d) duration.value = d;
  }
  bitrate.value = p.bitrate_mode === 'high' ? 'high' : 'standard';
  snackbar.showSuccess(t('seedance.results.toastSettingsApplied'));
}

// -------- 素材放大预览 --------
const assetPreview = ref<{ url: string; format: string } | null>(null);

// 素材方块 hover 预览：视频只在 hover 期间播放，避免隐藏状态常驻消耗
function onAssetThumbEnter(e: MouseEvent) {
  (e.currentTarget as HTMLElement)
    .querySelector<HTMLVideoElement>('.asset-hover-preview video')
    ?.play()
    .catch(() => {});
}
function onAssetThumbLeave(e: MouseEvent) {
  (e.currentTarget as HTMLElement)
    .querySelector<HTMLVideoElement>('.asset-hover-preview video')
    ?.pause();
}

// -------- Output 设置面板（PC 贴面板右侧弹出，小屏全屏弹窗） --------
const outputOpen = ref(false);
const isDesktop = ref(false);
let mqDesktop: MediaQueryList | null = null;
const updateMqDesktop = () => { isDesktop.value = mqDesktop?.matches ?? false; };
// 取值依据同素材选择侧板：panelRef.getBoundingClientRect().right + 12px，
// top 对齐面板顶部（≥12px），maxHeight 用剩余视口高；resize 重算。
const outputSideStyle = ref<Record<string, string>>({});
function computeOutputPos() {
  if (typeof window === 'undefined') return;
  const el = panelRef.value;
  if (!el) {
    outputSideStyle.value = { top: '80px', left: 'calc(50% - 210px)', maxHeight: 'calc(100vh - 92px)' };
    return;
  }
  const rect = el.getBoundingClientRect();
  const top = Math.max(rect.top, 12);
  const left = Math.min(rect.right + 12, window.innerWidth - 432);
  outputSideStyle.value = {
    top: `${top}px`,
    left: `${Math.max(left, 12)}px`,
    maxHeight: `calc(100vh - ${top + 12}px)`,
  };
}
watch(outputOpen, (v) => { if (v) computeOutputPos(); });
onMounted(() => {
  mqDesktop = window.matchMedia('(min-width: 1024px)');
  updateMqDesktop();
  mqDesktop.addEventListener('change', updateMqDesktop);
  window.addEventListener('resize', computeOutputPos);
});
onBeforeUnmount(() => {
  mqDesktop?.removeEventListener('change', updateMqDesktop);
  window.removeEventListener('resize', computeOutputPos);
});

defineExpose({ generate, applyTemplate, resetTemplate, applyExample, applyFromOrder });
</script>

<template>
  <div ref="panelRef" class="panel">
    <!-- Header -->
    <div class="head">
      <h2>{{ $t('seedance.create.studioTitle') }}</h2>
    </div>

    <!-- Mode toggle — Reference（多媒体参考，不传素材即纯文生视频）/ Keyframe（首尾帧） -->
    <div class="section">
      <div class="seg">
        <button
          :class="{ active: mode === 'image' }"
          :title="$t('seedance.create.modeReferenceTip')"
          @click="mode = 'image'"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="4"/><path d="M16 8v5a3 3 0 006 0v-1a10 10 0 10-3.92 7.94"/>
          </svg>
          <span>{{ $t('seedance.create.modeReference') }}</span>
        </button>
        <button
          :class="{ active: mode === 'frame' }"
          :title="$t('seedance.create.modeKeyframeTip')"
          @click="mode = 'frame'"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="M21 15l-5-5L5 21"/>
          </svg>
          <span>{{ $t('seedance.create.modeKeyframe') }}</span>
        </button>
      </div>
    </div>

    <!-- Model — 卡片触发器 + 下拉面板：2.0 默认可用，Fast/Mini/2.5 全部 Coming Soon（放在模式切换下方） -->
    <div class="section model-select-wrap">
      <button type="button" class="model-card" @click="modelOpen = !modelOpen">
        <span class="model-icon">
          <!-- 均衡器风格图标 — 深色圆角方块内四根蓝青色竖条 -->
          <svg v-if="isH3" viewBox="0 0 32 32" fill="none" aria-hidden="true">
            <rect width="32" height="32" rx="10" fill="#232323"/>
            <path d="M26 16.0001C26 10.4778 21.5003 5.98621 15.9744 6.00003C10.4917 6.01395 6.01336 10.4917 6.00003 15.9744C5.98617 21.4998 10.4773 26 16 26H22.5709C24.4614 26 25.9941 24.4672 25.9941 22.5768V16.3333C25.9979 16.2229 26 16.112 26 16.0001ZM10.8574 21.5536C9.26547 20.1947 8.28451 18.0206 8.32982 15.8039C8.33302 15.6487 8.34102 15.4931 8.35435 15.3384L8.35541 15.3283C8.58572 12.6834 10.2939 10.2166 12.6887 9.0709C15.0836 7.92522 18.0766 8.14383 20.28 9.6243C22.4834 11.1048 23.8157 13.794 23.66 16.4441C23.6259 17.0241 23.5257 17.5994 23.386 18.1635C23.3306 18.3879 23.2634 18.6204 23.1045 18.7883C22.9456 18.9563 22.6674 19.0341 22.4791 18.8997C22.2995 18.7718 22.2729 18.5201 22.2574 18.3005C22.1331 16.5215 21.6581 14.6875 20.4559 13.3706C19.548 12.3763 18.2578 11.7435 16.917 11.6193C15.5761 11.4951 14.1969 11.8779 13.1024 12.6621C12.1871 13.3184 11.4673 14.2573 11.1197 15.3288C10.7721 16.4004 10.8078 17.6005 11.2647 18.6306C11.6001 19.3865 12.1571 20.0428 12.8486 20.497C14.0711 21.2999 15.7441 21.414 17.0145 20.689C18.285 19.9639 19.0441 18.3911 18.7206 16.9644C18.397 15.5378 16.9511 14.4251 15.4961 14.5755C15.2739 14.5984 14.9561 14.5745 14.9481 14.351C14.9417 14.1783 15.1491 14.0871 15.3191 14.0568C16.7954 13.7918 18.3767 14.5414 19.1865 15.8039C19.9964 17.0663 20.0374 18.7574 19.3764 20.1035C18.7152 21.4497 17.4021 22.4334 15.9451 22.7884C13.981 23.2671 12.2025 22.7009 10.859 21.5536H10.8574Z" fill="url(#minimax-brand-gradient)"/>
            <defs>
              <linearGradient id="minimax-brand-gradient" x1="6.44893" y1="6.7369" x2="28.906" y2="28.518" gradientUnits="userSpaceOnUse">
                <stop offset="0.09" stop-color="#FFAB0C"/><stop offset="0.31" stop-color="#FF5538"/>
                <stop offset="0.46" stop-color="#E9405D"/><stop offset="0.75" stop-color="#D266DA"/>
                <stop offset="0.89" stop-color="#D584EF"/>
              </linearGradient>
            </defs>
          </svg>
          <svg v-else viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <rect x="1.5" y="7" width="3" height="8" rx="1.5" fill="#3B82F6"/>
            <rect x="6.5" y="4" width="3" height="12" rx="1.5" fill="#60A5FA"/>
            <rect x="11.5" y="9" width="3" height="6" rx="1.5" fill="#22D3EE"/>
            <rect x="16.5" y="6" width="3" height="9" rx="1.5" fill="#2DD4BF"/>
          </svg>
        </span>
        <span class="model-card-text">
          <span class="model-card-label">{{ $t('seedance.create.modelLabel') }}</span>
          <span class="model-card-name">
            {{ currentModelOption.label }}
            <span
              v-if="currentModelOption.badge"
              class="model-badge"
              :class="`model-badge-${currentModelOption.badge.variant}`"
            >{{ currentModelOption.badge.text }}</span>
          </span>
        </span>
        <svg
          class="model-chevron"
          :class="{ open: modelOpen }"
          width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
        ><polyline points="9 18 15 12 9 6"/></svg>
      </button>
      <div v-if="modelOpen" class="model-backdrop" @click="modelOpen = false" />
      <div v-if="modelOpen" class="model-panel">
        <button
          v-for="opt in MODEL_OPTIONS"
          :key="opt.value"
          type="button"
          class="model-opt"
          :class="{ selected: opt.value === model, disabled: opt.comingSoon }"
          :disabled="opt.comingSoon"
          @click="pickModel(opt)"
        >
          <!-- 禁用态只让行内容变淡，不影响 hover 浮层的不透明度 -->
          <span class="model-opt-main" :class="{ dim: opt.comingSoon }">
            <span class="model-icon sm">
              <svg v-if="opt.value === 'minimax_h3'" viewBox="0 0 32 32" fill="none" aria-hidden="true">
                    <rect width="32" height="32" rx="10" fill="#232323"/>
                    <path d="M26 16.0001C26 10.4778 21.5003 5.98621 15.9744 6.00003C10.4917 6.01395 6.01336 10.4917 6.00003 15.9744C5.98617 21.4998 10.4773 26 16 26H22.5709C24.4614 26 25.9941 24.4672 25.9941 22.5768V16.3333C25.9979 16.2229 26 16.112 26 16.0001ZM10.8574 21.5536C9.26547 20.1947 8.28451 18.0206 8.32982 15.8039C8.33302 15.6487 8.34102 15.4931 8.35435 15.3384L8.35541 15.3283C8.58572 12.6834 10.2939 10.2166 12.6887 9.0709C15.0836 7.92522 18.0766 8.14383 20.28 9.6243C22.4834 11.1048 23.8157 13.794 23.66 16.4441C23.6259 17.0241 23.5257 17.5994 23.386 18.1635C23.3306 18.3879 23.2634 18.6204 23.1045 18.7883C22.9456 18.9563 22.6674 19.0341 22.4791 18.8997C22.2995 18.7718 22.2729 18.5201 22.2574 18.3005C22.1331 16.5215 21.6581 14.6875 20.4559 13.3706C19.548 12.3763 18.2578 11.7435 16.917 11.6193C15.5761 11.4951 14.1969 11.8779 13.1024 12.6621C12.1871 13.3184 11.4673 14.2573 11.1197 15.3288C10.7721 16.4004 10.8078 17.6005 11.2647 18.6306C11.6001 19.3865 12.1571 20.0428 12.8486 20.497C14.0711 21.2999 15.7441 21.414 17.0145 20.689C18.285 19.9639 19.0441 18.3911 18.7206 16.9644C18.397 15.5378 16.9511 14.4251 15.4961 14.5755C15.2739 14.5984 14.9561 14.5745 14.9481 14.351C14.9417 14.1783 15.1491 14.0871 15.3191 14.0568C16.7954 13.7918 18.3767 14.5414 19.1865 15.8039C19.9964 17.0663 20.0374 18.7574 19.3764 20.1035C18.7152 21.4497 17.4021 22.4334 15.9451 22.7884C13.981 23.2671 12.2025 22.7009 10.859 21.5536H10.8574Z" fill="url(#minimax-brand-gradient)"/>
                    <defs>
                      <linearGradient id="minimax-brand-gradient" x1="6.44893" y1="6.7369" x2="28.906" y2="28.518" gradientUnits="userSpaceOnUse">
                        <stop offset="0.09" stop-color="#FFAB0C"/><stop offset="0.31" stop-color="#FF5538"/>
                        <stop offset="0.46" stop-color="#E9405D"/><stop offset="0.75" stop-color="#D266DA"/>
                        <stop offset="0.89" stop-color="#D584EF"/>
                      </linearGradient>
                    </defs>
              </svg>
              <svg v-else viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <rect x="1.5" y="7" width="3" height="8" rx="1.5" fill="#3B82F6"/>
                <rect x="6.5" y="4" width="3" height="12" rx="1.5" fill="#60A5FA"/>
                <rect x="11.5" y="9" width="3" height="6" rx="1.5" fill="#22D3EE"/>
                <rect x="16.5" y="6" width="3" height="9" rx="1.5" fill="#2DD4BF"/>
              </svg>
            </span>
            <span class="model-opt-text">
              <span class="model-opt-name">
                {{ opt.label }}
                <span
                  v-if="opt.badge"
                  class="model-badge"
                  :class="`model-badge-${opt.badge.variant}`"
                >{{ opt.badge.text }}</span>
                <span v-if="opt.comingSoon" class="soon-badge">{{ $t('seedance.common.comingSoon') }}</span>
              </span>
              <span class="model-opt-desc">{{ opt.shortDescription }}</span>
            </span>
          </span>
          <svg
            v-if="opt.value === model"
            class="model-check"
            width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
          ><polyline points="20 6 9 17 4 12"/></svg>
          <!-- hover 浮层：完整介绍文案 -->
          <span class="model-opt-tip">{{ opt.description }}</span>
        </button>
      </div>
    </div>

    <!-- Frame slots (Frame-to-Video mode) — 空槽整块可点，打开 Library 选择侧板 -->
    <div v-if="mode === 'frame'" class="section">
      <h3>{{ $t('seedance.create.frameSectionLabel') }} <span class="hint">{{ $t('seedance.create.frameSectionHint') }}</span></h3>
      <div class="frame-row">
        <div class="frame-slot">
          <div v-if="startFrame" class="slot-inner filled">
            <div class="tile-media"><img :src="startFrame" :alt="$t('seedance.create.altStartFrame')" /></div>
            <button class="tile-x" :aria-label="$t('seedance.common.remove')" @click="startFrame = ''">✕</button>
          </div>
          <button v-else type="button" class="slot-open" @click="openImagePicker('startFrame')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
            <span>{{ $t('seedance.create.startFrame') }}</span>
          </button>
        </div>
        <div class="arrow">→</div>
        <div class="frame-slot">
          <div v-if="endFrame" class="slot-inner filled">
            <div class="tile-media"><img :src="endFrame" :alt="$t('seedance.create.altEndFrame')" /></div>
            <button class="tile-x" :aria-label="$t('seedance.common.remove')" @click="endFrame = ''">✕</button>
          </div>
          <button v-else type="button" class="slot-open" @click="openImagePicker('endFrame')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
            <span>{{ $t('seedance.create.endFrame') }} {{ $t('seedance.common.optionalFull') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Reference Assets (Image-to-Video mode; text mode has no refs) -->
    <template v-else-if="mode === 'image'">
      <div class="section">
        <div class="ref-head">
          <h3>{{ $t('seedance.create.referenceImages') }}</h3>
        </div>
        <!-- 统一上传卡片：空态气泡引导，有素材后只剩网格；底部常驻上限文案。
             点击不再直接弹本地文件选择，改为打开 Library 选择面板（面板内可上传） -->
        <div class="up-card" :class="{ 'up-card-empty': !hasRefs }">
          <button
            v-if="!hasRefs"
            type="button"
            class="up-empty"
            @click="openMediaPicker"
          >
            <div class="bubble-row">
              <div class="bubble">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="M21 15l-5-5L5 21"/></svg>
              </div>
              <div class="bubble">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
              </div>
              <div class="bubble">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
              </div>
              <div class="bubble">
                <!-- + 号间歇旋转引导上传：每 4.5s 转半圈后静止；
                     + 有旋转对称性，循环重置 180°→0° 无视觉跳变 -->
                <svg class="plus-twirl" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </div>
            </div>
            <div class="up-text">
              <p class="up-line1">{{ $t('seedance.common.uploadMedia') }}</p>
              <p class="up-line2">{{ $t('seedance.create.uploadMediaHint') }}</p>
            </div>
          </button>
          <div v-else class="ref-grid">
            <!-- overflow 裁切收到 .tile-media 内层，让 X 能悬出方块右上角 -->
            <!-- 点击放大预览；底部 @imageN 引用名；hover 黑遮罩 + 放大图标 -->
            <div
              v-for="(u, i) in refImages"
              :key="u"
              class="ref-tile"
              @mouseenter="onAssetThumbEnter"
              @mouseleave="onAssetThumbLeave"
            >
              <button type="button" class="tile-media tile-open" @click="assetPreview = { url: u, format: 'photo' }">
                <img :src="u" :alt="`Image ${i+1}`" />
                <span class="tile-hover"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg></span>
                <span class="tile-ref-label">@image{{ i + 1 }}</span>
              </button>
              <button class="tile-x" :aria-label="$t('seedance.common.remove')" @click="removeRefImage(i)">✕</button>
              <!-- hover 延迟 0.5s 浮出的 144px 方形预览（移开立即消失） -->
              <span class="asset-hover-preview" aria-hidden="true"><img :src="u" alt="" /></span>
            </div>
            <div
              v-for="(u, i) in refVideos"
              :key="u"
              class="ref-tile"
              @mouseenter="onAssetThumbEnter"
              @mouseleave="onAssetThumbLeave"
            >
              <button type="button" class="tile-media tile-open" @click="assetPreview = { url: u, format: 'video' }">
                <video :src="u" muted playsinline preload="metadata" class="ref-video" />
                <span class="tile-hover"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg></span>
                <span class="tile-ref-label">@video{{ i + 1 }}</span>
              </button>
              <button class="tile-x" :aria-label="$t('seedance.common.remove')" @click="removeRefVideo(i)">✕</button>
              <!-- hover 延迟 0.5s 浮出的 144px 方形预览，hover 期间播放 -->
              <span class="asset-hover-preview" aria-hidden="true"><video :src="u" loop muted playsinline preload="metadata" /></span>
            </div>
            <!-- 音频素材 —— 只留音符图标 + @audioN -->
            <div v-for="(u, i) in refAudios" :key="u" class="ref-tile">
              <button type="button" class="tile-open ref-audio-tile" @click="assetPreview = { url: u, format: 'audio' }">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                <span class="tile-hover"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg></span>
                <span class="tile-ref-label">@audio{{ i + 1 }}</span>
              </button>
              <button class="tile-x" :aria-label="$t('seedance.common.remove')" @click="removeRefAudio(i)">✕</button>
            </div>
            <button
              class="ref-add"
              @click="openMediaPicker"
              :title="$t('seedance.create.addReferenceTip')"
            >+</button>
          </div>
          <p class="ref-limit-hint">{{ $t('seedance.create.refLimitsHint') }}</p>
        </div>
      </div>
    </template>

    <!-- Prompt -->
    <div class="section prompt-section">
      <div class="prompt-head">
        <h3>{{ $t('seedance.create.promptLabel') }}</h3>
        <div class="prompt-head-actions">
          <!-- 一键应用官方示例（prompt + 素材 + 输出设置），再点清空 -->
          <button
            v-if="mode === 'image' && EXAMPLE_TEMPLATE"
            type="button"
            class="eg-btn"
            :class="{ active: isExampleActive }"
            :title="isExampleActive ? $t('seedance.create.clearExampleTip') : $t('seedance.create.applyExampleTip')"
            @click="toggleExample"
          >
            ✨ {{ $t('seedance.create.exampleButton') }}
          </button>
        </div>
      </div>
      <!-- 独立 relative 包编辑器 + popover，让弹窗按光标/chip 锚点精准定位 -->
      <div :ref="mainEditor.setWrapEl" class="textarea-wrap">
        <div class="prompt-box">
          <!-- contenteditable 富文本框：@image1 等 token 渲染成
               「缩略图 + token + 底色」的原子 chip，高度随内容自增 -->
          <div
            :ref="mainEditor.setEl"
            class="prompt-editor"
            contenteditable="true"
            role="textbox"
            aria-multiline="true"
            :aria-label="$t('seedance.create.promptLabel')"
            @input="mainEditor.onInput"
            @keydown="mainEditor.onKeyDown"
            @paste="mainEditor.onPaste"
            @blur="mainEditor.onBlur"
            @click="mainEditor.onClick"
          />
          <div class="prompt-toolbar">
            <div class="toolbar-left">
              <!-- @ 引用只在 Reference 模式（有参考素材）下有意义 -->
              <span v-if="mode === 'image'" class="tipwrap">
                <!-- mousedown 不抢焦点 —— 编辑器不失焦，@ 才能插在光标处而非开头 -->
                <button type="button" class="tool-btn" :aria-label="$t('seedance.create.mentionAria')" @mousedown.prevent @click="mainEditor.insertAtSymbol">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="4"/><path d="M16 8v5a3 3 0 006 0v-1a10 10 0 10-3.92 7.94"/>
                  </svg>
                </button>
                <span class="tipbubble">{{ $t('seedance.create.mentionTip') }}</span>
              </span>
              <!-- Prompt from Image —— @ 右边，两个模式都显示 -->
              <span class="tipwrap">
                <button type="button" class="tool-btn" :aria-label="$t('seedance.promptFromImage.buttonTip')" @mousedown.prevent @click="promptFromImageOpen = true">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z"/></svg>
                </button>
                <span class="tipbubble">{{ $t('seedance.promptFromImage.buttonTip') }}</span>
              </span>
            </div>
            <div class="toolbar-right">
              <span v-if="promptTooLong" class="tipwrap">
                <span class="plen-bad counter-warn">{{ $t('seedance.create.charCounter', { count: prompt.length, label: $t('seedance.create.promptTooLong') }) }}</span>
                <span class="tipbubble tipbubble-right tipbubble-wide">
                  {{ $t('seedance.create.promptLengthTip') }}
                </span>
              </span>
              <span class="tipwrap">
                <button type="button" class="tool-btn" :aria-label="$t('seedance.create.fullscreenAria')" @click="fullscreenOpen = true">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                  </svg>
                </button>
                <span class="tipbubble tipbubble-right">{{ $t('seedance.create.fullscreenTip') }}</span>
              </span>
            </div>
          </div>
        </div>
        <!-- contenteditable 没有原生 placeholder —— frame 模式画一个纯文本覆盖层 -->
        <div v-if="mode === 'frame' && prompt === ''" class="ph-overlay" aria-hidden="true">
          {{ $t('seedance.create.placeholderFrame') }}
        </div>
        <!-- Image 模式的自定义 placeholder — 原生 placeholder 无法给 @ 单独上色 -->
        <div v-if="mode === 'image' && prompt === ''" class="ph-overlay" aria-hidden="true">
          {{ $t('seedance.create.placeholderImagePrefix') }}
          <span class="tipwrap">
            <button type="button" tabindex="-1" class="ph-at" @mousedown.prevent @click="mainEditor.insertAtSymbol">@</button>
            <span class="tipbubble tipbubble-wide">{{ $t('seedance.create.mentionHint') }}</span>
          </span>
          {{ $t('seedance.create.placeholderImageSuffix') }}
        </div>
        <!-- @ 素材引用选择弹层（跟随光标/chip 锚点，空间不足翻转到上方） -->
        <div
          v-if="mainEditor.showMentions"
          class="mention-popover"
          :class="{ above: mainEditor.mentionPlacement === 'above' }"
          :style="{ top: `${mainEditor.mentionPos.top}px`, left: `${mainEditor.mentionPos.left}px` }"
        >
          <div class="mention-head">{{ $t('seedance.create.mentionPopoverTitle') }}</div>
          <!-- 最多露出 7 个半选项（48px/项），多出来的滚动查看 -->
          <ul :ref="mainEditor.setListEl">
            <li v-for="(opt, i) in mentionOptions" :key="opt.token">
              <button
                type="button"
                :class="{ active: i === mainEditor.activeIdx }"
                @mousedown.prevent="mainEditor.chooseOption(opt.token)"
                @mouseenter="mainEditor.activeIdx = i"
              >
                <!-- 每项展示素材缩略图（音频用音符图标兜底）+ 规范 token -->
                <video
                  v-if="opt.asset.format === 'video'"
                  class="m-thumb"
                  :src="opt.asset.url"
                  autoplay
                  loop
                  muted
                  playsinline
                />
                <span v-else-if="opt.asset.format === 'audio'" class="m-thumb m-thumb-audio">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                </span>
                <img v-else class="m-thumb" :src="opt.asset.url" alt="" />
                <span class="mt">{{ opt.token }}</span>
                <!-- 类型图标 —— 快速区分图/视频/音频 -->
                <svg v-if="opt.asset.format === 'audio'" class="m-type" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                <svg v-else-if="opt.asset.format === 'video'" class="m-type" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                <svg v-else class="m-type" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="M21 15l-5-5L5 21"/></svg>
              </button>
            </li>
          </ul>
        </div>
      </div>

      <!-- 全屏编辑弹窗 — 编辑同一个 prompt（与主输入框同款 chip 编辑器，双向同步） -->
      <Teleport to="body">
        <div v-if="fullscreenOpen" class="fs-overlay" @click="fullscreenOpen = false">
          <div class="fs-dialog" @click.stop>
            <div class="fs-head">
              <h2>{{ $t('seedance.create.promptLabel') }}</h2>
              <div class="fs-head-right">
                <span v-if="promptTooLong" class="plen-bad counter-warn">{{ $t('seedance.create.charCounter', { count: prompt.length, label: $t('seedance.create.promptTooLong') }) }}</span>
                <button type="button" class="fs-close" :aria-label="$t('seedance.common.close')" @click="fullscreenOpen = false">✕</button>
              </div>
            </div>
            <!-- 打开自动聚焦到末尾 -->
            <div :ref="fsEditor.setWrapEl" class="fs-editor-wrap">
              <div
                :ref="fsEditor.setEl"
                class="prompt-editor fs-editor"
                contenteditable="true"
                role="textbox"
                aria-multiline="true"
                :aria-label="$t('seedance.create.promptLabel')"
                @input="fsEditor.onInput"
                @keydown="fsEditor.onKeyDown"
                @paste="fsEditor.onPaste"
                @blur="fsEditor.onBlur"
                @click="fsEditor.onClick"
              />
              <!-- contenteditable 没有原生 placeholder -->
              <div v-if="prompt === ''" class="fs-ph" aria-hidden="true">{{ $t('seedance.create.placeholderFullscreen') }}</div>
              <!-- @ 素材引用选择弹层（同主输入框；底部空间不足自动翻转到上方） -->
              <div
                v-if="fsEditor.showMentions"
                class="mention-popover"
                :class="{ above: fsEditor.mentionPlacement === 'above' }"
                :style="{ top: `${fsEditor.mentionPos.top}px`, left: `${fsEditor.mentionPos.left}px` }"
              >
                <div class="mention-head">{{ $t('seedance.create.mentionPopoverTitle') }}</div>
                <ul :ref="fsEditor.setListEl">
                  <li v-for="(opt, i) in mentionOptions" :key="opt.token">
                    <button
                      type="button"
                      :class="{ active: i === fsEditor.activeIdx }"
                      @mousedown.prevent="fsEditor.chooseOption(opt.token)"
                      @mouseenter="fsEditor.activeIdx = i"
                    >
                      <video
                        v-if="opt.asset.format === 'video'"
                        class="m-thumb"
                        :src="opt.asset.url"
                        autoplay
                        loop
                        muted
                        playsinline
                      />
                      <span v-else-if="opt.asset.format === 'audio'" class="m-thumb m-thumb-audio">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                      </span>
                      <img v-else class="m-thumb" :src="opt.asset.url" alt="" />
                      <span class="mt">{{ opt.token }}</span>
                      <svg v-if="opt.asset.format === 'audio'" class="m-type" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                      <svg v-else-if="opt.asset.format === 'video'" class="m-type" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                      <svg v-else class="m-type" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="M21 15l-5-5L5 21"/></svg>
                    </button>
                  </li>
                </ul>
              </div>
            </div>
            <div v-if="promptTooLong" class="fs-footnote">
              {{ $t('seedance.create.promptLengthWarning') }}
            </div>
          </div>
        </div>
      </Teleport>
    </div>

    <!-- Output 卡片触发器 —— 汇总展示设置，点击打开右侧设置面板 -->
    <div class="section">
      <button type="button" class="model-card" @click="outputOpen = true">
        <span class="model-icon">
          <!-- 滑杆图标 -->
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="21" y1="4" x2="14" y2="4"/><line x1="10" y1="4" x2="3" y2="4"/><line x1="21" y1="12" x2="12" y2="12"/><line x1="8" y1="12" x2="3" y2="12"/><line x1="21" y1="20" x2="16" y2="20"/><line x1="12" y1="20" x2="3" y2="20"/><line x1="14" y1="2" x2="14" y2="6"/><line x1="8" y1="10" x2="8" y2="14"/><line x1="16" y1="18" x2="16" y2="22"/></svg>
        </span>
        <span class="model-card-text">
          <span class="model-card-label">{{ $t('seedance.create.outputLabel') }}</span>
          <span class="model-card-name">{{ aspect }} | {{ resolution }} | {{ duration }}s{{ batchCount > 1 ? ` | ×${batchCount}` : '' }}</span>
        </span>
        <svg
          class="model-chevron"
          width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
        ><polyline points="9 18 15 12 9 6"/></svg>
      </button>
    </div>

    <div v-if="errorMsg" class="err">{{ errorMsg }}</div>

    <!-- Output 设置面板 —— PC 贴面板右侧弹出（高度随内容），小屏全屏弹窗 -->
    <Teleport to="body">
      <div v-if="outputOpen" class="out-mask" :class="{ 'out-mask-side': isDesktop }" @click.self="outputOpen = false">
        <div class="out-dialog" :class="{ 'out-dialog-side': isDesktop }" :style="isDesktop ? outputSideStyle : undefined">
          <div class="out-head">
            <h2>{{ $t('seedance.create.outputSettingsTitle') }}</h2>
            <button type="button" class="out-close" :aria-label="$t('seedance.common.close')" @click="outputOpen = false">✕</button>
          </div>
          <div class="out-body">
            <!-- Aspect ratio -->
            <div class="psec out-sec">
              <h3>{{ $t('seedance.create.aspectLabel') }}</h3>
              <div class="p-grid cols-6">
                <button
                  v-for="a in ASPECTS" :key="a"
                  type="button"
                  class="pbtn ratio-btn"
                  :class="{ active: aspect === a }"
                  @click="aspect = a"
                >
                  <span class="ratio-frame"><span class="ratio-box" :style="ratioBox(a)" /></span>
                  <span class="ratio-label">{{ a }}</span>
                </button>
              </div>
            </div>
            <!-- Duration -->
            <div class="psec out-sec">
              <h3>{{ $t('seedance.create.durationLabel') }}</h3>
              <div class="p-grid cols-6">
                <button
                  v-for="d in durations" :key="d"
                  type="button"
                  class="pbtn"
                  :class="{ active: duration === d }"
                  @click="duration = d"
                >{{ d }}s</button>
              </div>
            </div>
            <!-- Resolution -->
            <div class="psec out-sec">
              <h3>{{ $t('seedance.create.resolutionLabel') }}</h3>
              <div class="p-grid" :style="{ gridTemplateColumns: `repeat(${resolutions.length}, minmax(0, 1fr))` }">
                <button
                  v-for="r in resolutions" :key="r"
                  type="button"
                  class="pbtn"
                  :class="{ active: resolution === r }"
                  @click="resolution = r"
                >{{ r }}</button>
              </div>
            </div>
            <!-- Bitrate (2.0 family only — Fast/Mini share 2.0 params) -->
            <div v-if="isV20" class="psec out-sec">
              <h3>{{ $t('seedance.create.bitrateLabel') }}</h3>
              <div class="p-grid cols-2">
                <button type="button" class="pbtn" :class="{ active: bitrate === 'standard' }" @click="bitrate = 'standard'">{{ $t('seedance.videos.bitrateStandard') }}</button>
                <button type="button" class="pbtn" :class="{ active: bitrate === 'high' }" @click="bitrate = 'high'">{{ $t('seedance.videos.bitrateHigh') }}</button>
              </div>
            </div>
            <!-- Output Number（原 Batch Count 步进器） -->
            <div class="psec out-sec">
              <h3>{{ $t('seedance.create.outputNumberLabel') }}</h3>
              <div class="p-grid cols-4">
                <button
                  v-for="n in [1, 2, 3, 4]" :key="n"
                  type="button"
                  class="pbtn"
                  :class="{ active: batchCount === n }"
                  @click="batchCount = n"
                >{{ n }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 素材放大预览 -->
    <Teleport to="body">
      <div v-if="assetPreview" class="preview-overlay" @click="assetPreview = null">
        <button type="button" class="preview-close" :aria-label="$t('seedance.common.closePreview')" @click="assetPreview = null">✕</button>
        <video
          v-if="assetPreview.format === 'video'"
          :src="assetPreview.url"
          controls
          autoplay
          playsinline
          class="preview-media"
          @click.stop
        />
        <div v-else-if="assetPreview.format === 'audio'" class="preview-audio" @click.stop>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
          <p>{{ assetPreview.url.split('/').pop() }}</p>
          <audio :src="assetPreview.url" controls autoplay />
        </div>
        <img v-else :src="assetPreview.url" alt="" class="preview-media" @click.stop />
      </div>
    </Teleport>

    <!-- 桌面端：生成按钮 sticky 固定在设置面板底部（面板内部滚动、整页不滚）；
         移动端隐藏，改用外部常驻底栏 EditorBridgeGenerate -->
    <div class="panel-footer">
      <button class="gen-cta" :disabled="!canGenerate || submitting || polling" @click="generate">
        <span class="spark">✦</span>
        <span class="flex-1 text-left">{{ footerLabel }}</span>
        <span class="cost">{{ t('seedance.create.creditsBadge', { count: estimatedCredits }) }}</span>
      </button>
    </div>
  </div>

  <!-- 素材/帧图选择统一用 PC 侧板形态贴面板右侧（小屏自动退回全屏弹窗）
       Reference Assets 走直接添加模式（点击即加/再点移除、✓角标）；帧图保持单选 -->
  <AssetPickerDialog
    v-model="showAssetPicker"
    :type="pickerSharedType"
    :multiple="assetPickerMulti"
    variant="side"
    :anchor="panelRef"
    :reference-assets="referenceAssetsForBudget"
    :added-urls="addedRefUrls"
    @pick="onAssetPicked"
    @remove-added="onRefRemoveByUrl"
  />

  <!-- 从图片生成提示词 -->
  <PromptFromImageDialog v-model="promptFromImageOpen" @use="onPromptFromImage" />

  <!-- prompt 未引用全部素材：自定义确认弹窗（替代原生 window.confirm） -->
  <Teleport to="body">
    <div v-if="missingRefDialog" class="mr-overlay" @click.self="resolveMissingRef(false)">
      <div class="mr-dialog">
        <h3>{{ t('seedance.create.missingRefTitle') }}</h3>
        <p>{{ t('seedance.create.missingRefBody', { tokens: missingRefDialog.tokens }) }}</p>
        <div class="mr-actions">
          <button type="button" class="mr-btn ghost" @click="resolveMissingRef(false)">
            {{ t('seedance.common.cancel') }}
          </button>
          <button type="button" class="mr-btn primary" @click="resolveMissingRef(true)">
            {{ t('seedance.create.missingRefContinue') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.panel {
  @apply flex flex-col gap-3.5 rounded-2xl bg-neutral-900/70 border border-white/5 p-4 md:p-5;
  /* 桌面端：面板作为有界高度的内部滚动容器（父级 .middle 提供高度），
     底部生成按钮 sticky；md:pb-0 让 sticky footer 贴到面板底边。 */
  @apply md:min-h-0 md:flex-1 md:overflow-y-auto md:pb-0;
}

/* 生成按钮 footer：桌面端 sticky 在面板底部；移动端隐藏（用外部固定底栏） */
.panel-footer { @apply hidden; }
@media (min-width: 768px) {
  .panel-footer {
    @apply block sticky bottom-0 z-10;
    /* 取消面板 md:p-5 横向内边距 + 抵消 md:pb-0 后的底部，铺满并贴底 */
    margin: 0.25rem -1.25rem 0;
    padding: 0.75rem 1.25rem 1.25rem;
    background: rgba(20, 20, 20, 0.95);
    backdrop-filter: blur(8px);
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    border-bottom-left-radius: 1rem;
    border-bottom-right-radius: 1rem;
  }
}
.gen-cta {
  @apply flex items-center gap-2 w-full rounded-full px-6 py-3 text-base font-semibold text-white;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 50%, #c2410c 100%);
  box-shadow: 0 8px 24px -8px rgba(249, 115, 22, 0.5);
  transition: all 0.2s;
}
.gen-cta:disabled { opacity: 0.4; cursor: not-allowed; }
.gen-cta .spark { color: #fde68a; }
.gen-cta .cost {
  @apply rounded-full px-3 py-0.5 text-xs;
  background: rgba(0, 0, 0, 0.3);
}
.head { @apply flex items-center gap-2; }
h2 { @apply text-base md:text-lg font-bold text-white; margin: 0; }
h3 {
  @apply text-xs md:text-sm font-semibold text-white/70 flex items-center gap-1.5;
  margin: 0 0 0.5rem 0;
}
.hint { @apply text-[10px] font-normal text-white/40; }
.section { @apply flex flex-col gap-1; position: relative; }

/* Model select — 卡片触发器 + 下拉面板 */
.model-select-wrap { position: relative; }
.model-card {
  @apply flex w-full items-center gap-3 rounded-xl border border-white/10 p-3 text-left cursor-pointer transition;
  background: rgba(255, 255, 255, 0.05);
}
.model-card:hover { background: rgba(255, 255, 255, 0.09); }
.model-icon {
  @apply flex shrink-0 items-center justify-center rounded-lg border border-white/10;
  width: 40px; height: 40px;
  background: #0c0c0e; /* 深色方块底 */
}
.model-icon svg { width: 20px; height: 20px; }
.model-icon.sm { @apply rounded-md; width: 32px; height: 32px; }
.model-icon.sm svg { width: 16px; height: 16px; }
.model-card-text { @apply flex min-w-0 flex-1 flex-col; }
.model-card-label { @apply text-xs text-white/50; }
.model-card-name { @apply text-base font-semibold text-white; }
.model-chevron { @apply shrink-0 text-white/50 transition-transform; }
.model-chevron.open { transform: rotate(90deg); }
.model-backdrop { @apply fixed inset-0 z-40; }
.model-panel {
  @apply absolute inset-x-0 z-50 mt-2 rounded-xl border border-white/10 p-2 shadow-xl;
  top: 100%;
  background: #131316; /* 实底不透明 */
}
.model-opt {
  @apply relative flex w-full items-center gap-3 rounded-lg p-2.5 text-left border-none cursor-pointer transition;
  background: transparent;
}
.model-opt:hover:not(.disabled) { background: rgba(255, 255, 255, 0.07); }
.model-opt.selected { background: rgba(255, 255, 255, 0.07); }
.model-opt.disabled { cursor: not-allowed; }
/* 禁用态透明度只作用行内容（.model-opt-main），不作用到 hover 浮层 */
.model-opt-main { @apply flex min-w-0 flex-1 items-center gap-3; }
.model-opt-main.dim { opacity: 0.5; }
.model-opt-text { @apply flex min-w-0 flex-1 flex-col; }
.model-opt-name { @apply flex items-center gap-2 text-sm font-semibold text-white; }
.model-opt-desc { @apply mt-0.5 truncate text-xs text-white/50; }
.model-check { @apply shrink-0 text-white; }
.model-opt-tip {
  @apply pointer-events-none absolute rounded-md border border-white/10 px-3 py-2 text-xs font-normal leading-relaxed text-white opacity-0 shadow-md transition-opacity;
  z-index: 60;
  bottom: 100%; left: 50%;
  transform: translateX(-50%);
  margin-bottom: 6px;
  width: max-content; max-width: 340px;
  background: #171717; /* 实底不透明 */
  white-space: normal;
  text-align: left;
}
.model-opt:hover .model-opt-tip { opacity: 1; }

/* Seg buttons */
.seg {
  @apply flex gap-1.5 rounded-lg bg-black/40 border border-white/10 p-1;
}
.seg button {
  @apply flex flex-1 items-center justify-center gap-1.5 py-1.5 rounded-md text-xs md:text-sm text-white/60 hover:text-white transition;
  background: transparent;
  border: none;
  cursor: pointer;
}
.seg button.active {
  @apply text-white font-medium;
  background: linear-gradient(90deg, rgba(217, 70, 239, 0.35), rgba(244, 63, 94, 0.35));
}
.soon-badge {
  @apply text-[9px] rounded-full px-1.5 py-0.5 text-white/70 leading-none;
  background: rgba(255,255,255,0.08);
}
/* 模型徽章：NEW = 强调色胶囊(#d946ef)，PRO = 琥珀金胶囊(#f59e0b) */
.model-badge {
  @apply inline-block align-middle text-[9px] font-bold rounded-full px-1.5 py-0.5 leading-none;
}
.model-badge-new {
  background: rgba(217, 70, 239, 0.15);
  color: #e879f9;
}
.model-badge-pro {
  background: rgba(245, 158, 11, 0.15);
  color: #fbbf24;
}

/* Frame slots — 空槽整块可点打开 Library 侧板；已填槽 X 同素材网格样式 */
.frame-row {
  @apply flex items-center gap-2;
}
.frame-slot {
  @apply flex-1;
  min-width: 0;
}
/* overflow 裁切在 .tile-media 内层，外层不裁切，X 才能悬出角落 */
.slot-inner {
  @apply relative rounded-lg bg-black/40 border border-white/15;
  aspect-ratio: 1;
}
.slot-inner.filled { border-color: rgba(217, 70, 239, 0.4); }
.slot-open {
  @apply flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-white/15 text-white/50 text-[10px] cursor-pointer transition-colors;
  aspect-ratio: 1;
  background: rgba(0, 0, 0, 0.3);
}
.slot-open:hover { @apply text-white; background: rgba(0, 0, 0, 0.5); }
.arrow { @apply text-white/50 text-lg; }

/* Ref assets */
.ref-head {
  @apply flex items-center justify-between mb-1;
  h3 { margin: 0; }
}
.ref-grid {
  @apply grid gap-2;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}
/* overflow 裁切在 .tile-media 内层，外层 .ref-tile 不裁切，X 才能悬出角落 */
.ref-tile, .ref-add {
  @apply relative rounded-lg bg-black/40 border border-white/10;
  aspect-ratio: 1;
}
.ref-add { @apply overflow-hidden; }
.tile-media {
  @apply h-full w-full overflow-hidden rounded-lg;
}
.tile-media img,
.tile-media .ref-video { @apply w-full h-full object-cover; background: #000; }
.ref-add {
  @apply flex items-center justify-center text-white/50 hover:text-white hover:bg-black/60 text-2xl cursor-pointer border-dashed;
}
/* 素材方块 X：加大点击区、悬出右上角；hover 黑色加深；
   PC（hover:hover）默认隐藏、hover 单个方块时淡入，触屏常显 */
.tile-x {
  position: absolute;
  top: -6px; right: -6px;
  width: 22px; height: 22px;
  @apply flex items-center justify-center rounded-full border-none cursor-pointer;
  background: rgba(0, 0, 0, 0.8);
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px; line-height: 1;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  transition: opacity 0.15s, background 0.15s, color 0.15s;
}
.tile-x:hover { background: #000; color: #fff; }
@media (hover: hover) {
  .tile-x { opacity: 0; }
  .ref-tile:hover .tile-x,
  .slot-inner:hover .tile-x { opacity: 1; }
}
/* 点击放大预览的媒体按钮 + hover 黑遮罩/放大图标 + 底部 @ 引用名 */
.tile-open {
  @apply relative block p-0 border-none cursor-pointer;
  background: transparent;
}
.tile-hover {
  pointer-events: none;
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.4);
  color: white;
  opacity: 0;
  transition: opacity 0.15s;
}
.tile-open:hover .tile-hover { opacity: 1; }
.tile-ref-label {
  pointer-events: none;
  position: absolute; left: 0; right: 0; bottom: 6px;
  text-align: center;
  font-size: 12px; font-weight: 600;
  color: white;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
}
/* 音频方块 —— 只留音符图标 */
.ref-audio-tile {
  @apply flex h-full w-full flex-col items-center justify-center text-white/60 rounded-lg;
}
/* 图/视频素材块 hover 延迟 0.5s 浮出的 144px 方形预览（移开立即消失） */
.asset-hover-preview {
  pointer-events: none;
  position: absolute;
  bottom: 100%; left: 50%;
  transform: translateX(-50%);
  margin-bottom: 6px;
  z-index: 60;
  width: 144px; height: 144px;
  overflow: hidden;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: #131316;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.15s;
}
.ref-tile:hover .asset-hover-preview {
  opacity: 1;
  transition-delay: 0.5s;
}
.asset-hover-preview img,
.asset-hover-preview video {
  width: 100%; height: 100%;
  object-fit: cover;
}

/* 统一上传卡片 */
.up-card {
  @apply rounded-xl border border-white/10 p-3;
  background: rgba(255, 255, 255, 0.03);
}
.up-card-empty { @apply transition-colors; }
.up-card-empty:hover { background: rgba(255, 255, 255, 0.06); }
.up-empty {
  @apply flex w-full flex-col items-center gap-2.5 rounded-lg py-2.5 border-none cursor-pointer;
  background: transparent;
}
.up-empty:disabled { @apply cursor-not-allowed; }
.bubble-row { @apply flex; }
.bubble-row > .bubble + .bubble { margin-left: -8px; }
.bubble {
  @apply flex items-center justify-center rounded-full text-white/60;
  width: 40px; height: 40px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.02));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(4px);
}
/* + 号间歇旋转：0-86% 静止，86-100% 转半圈；+ 旋转对称，循环无跳变 */
@keyframes seedance-plus-twirl {
  0%, 86% { transform: rotate(0deg); }
  100% { transform: rotate(180deg); }
}
.plus-twirl { animation: seedance-plus-twirl 4.5s ease-in-out infinite; }
.up-text { @apply text-center; }
.up-line1 { @apply text-sm text-white/50; margin: 0; }
.up-line2 { @apply text-sm text-white/80; margin: 0; }
.ref-limit-hint {
  @apply mt-2 text-center text-[10px] text-white/40;
  margin-bottom: 0;
}

/* Prompt */
.prompt-box {
  @apply rounded-lg bg-black/40 border border-white/10;
}
/* 聚焦描边用较暗的中性色，不用亮的品牌色 */
.prompt-box:focus-within { border-color: rgba(255, 255, 255, 0.28); }
/* contenteditable 编辑器：高度随内容自增（无需镜像层） */
.prompt-editor {
  display: block;
  min-height: 5.5rem;
  padding: 0.6rem 0.75rem;
  font-size: 0.85rem;
  line-height: 1.5;
  font-family: inherit;
  color: white;
  white-space: pre-wrap;
  word-break: break-word;
  outline: none;
}
/* @mention chip（buildChipEl 命令式创建，scoped 需 :deep） */
.prompt-editor :deep(.m-chip) {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0 2px;
  padding: 1px 6px;
  border-radius: 6px;
  background: rgba(217, 70, 239, 0.15); /* 主题色 15% 底 */
  vertical-align: middle;
  font-size: 0.75rem;
  font-weight: 500;
  color: white;
  user-select: none;
  cursor: pointer;
}
.prompt-editor :deep(.m-chip:hover) { background: rgba(217, 70, 239, 0.25); }
/* token 文字用主题强调色，更醒目 */
.prompt-editor :deep(.m-chip-label) { color: #f0abfc; }
.prompt-editor :deep(.m-chip-thumbwrap) {
  position: relative;
  display: flex;
  width: 16px; height: 16px;
  flex-shrink: 0;
}
.prompt-editor :deep(.m-chip-thumb) {
  width: 100%; height: 100%;
  border-radius: 3px;
  object-fit: cover;
}
.prompt-editor :deep(.m-chip-icon) {
  display: flex; align-items: center; justify-content: center;
  width: 100%; height: 100%;
  color: #e879f9;
}
.prompt-editor :deep(.m-chip-icon svg) { width: 12px; height: 12px; }
/* 缩略图上 hover 浮出的 X —— 点击快速删除该引用（data-chip-remove 事件委托） */
.prompt-editor :deep(.m-chip-remove) {
  position: absolute; inset: 0;
  display: none;
  align-items: center; justify-content: center;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  cursor: pointer;
}
.prompt-editor :deep(.m-chip:hover .m-chip-remove) { display: flex; }
.prompt-editor :deep(.m-chip-remove svg) { width: 10px; height: 10px; }
/* hover 时 chip 上方浮出的 144px 方形预览（不随素材比例变化） */
.prompt-editor :deep(.m-chip-preview) {
  pointer-events: none;
  position: absolute;
  bottom: 100%; left: 50%;
  transform: translateX(-50%);
  margin-bottom: 6px;
  z-index: 60;
  display: none;
  width: 144px; height: 144px;
  overflow: hidden;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: #131316;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);
}
.prompt-editor :deep(.m-chip:hover .m-chip-preview) { display: block; }
.prompt-editor :deep(.m-chip-preview img),
.prompt-editor :deep(.m-chip-preview video) {
  width: 100%; height: 100%;
  object-fit: cover;
}
.prompt-editor :deep(.m-chip-preview-icon) {
  display: flex; align-items: center; justify-content: center;
  width: 100%; height: 100%;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.5);
}
.prompt-editor :deep(.m-chip-preview-icon svg) { width: 32px; height: 32px; }
.prompt-toolbar {
  @apply flex items-center justify-between px-1.5 pb-1.5;
}
.toolbar-left { @apply flex items-center; gap: 0.25rem; }
.toolbar-right { @apply flex items-center gap-2; }
.tool-btn {
  @apply rounded-md p-1.5 text-white/60 border-none cursor-pointer flex items-center justify-center;
  background: rgba(255, 255, 255, 0.06);
}
.tool-btn:hover { @apply text-white; background: rgba(255, 255, 255, 0.12); }
.counter-warn { @apply text-[10px] cursor-help; }

/* 纯 CSS 悬浮小标签 — hover 时在元素上方浮现提示文案 */
.tipwrap { @apply relative inline-flex; }
.tipbubble {
  @apply pointer-events-none absolute z-40 rounded-md border border-white/10 px-2.5 py-1.5 text-[10px] leading-relaxed text-white opacity-0 shadow-md transition-opacity;
  background: #171717;
  bottom: 100%;
  margin-bottom: 6px;
  left: 50%;
  transform: translateX(-50%);
  width: max-content;
  max-width: 340px;
}
.tipwrap:hover .tipbubble { opacity: 1; }
.tipbubble-right { left: auto; right: 0; transform: none; }
.tipbubble-wide { min-width: 220px; }
.t-red { @apply text-red-400; }
.t-green { @apply text-emerald-400; }

/* 非 text 模式空 prompt 的自定义 placeholder 覆盖层 */
.ph-overlay {
  @apply absolute text-sm text-white/40;
  top: 0.625rem;
  left: 0.625rem;
  right: 0.625rem;
  pointer-events: none;
  line-height: 1.5;
}
.ph-at {
  @apply inline-flex items-center justify-center rounded font-medium text-white/90 border-none cursor-pointer align-middle leading-none;
  pointer-events: auto;
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 4px 4px;
}
.ph-at:hover { background: rgba(255, 255, 255, 0.2); }

/* 全屏编辑弹窗 */
.fs-overlay {
  @apply fixed inset-0 flex items-center justify-center p-4;
  z-index: 100;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
}
.fs-dialog {
  @apply flex flex-col w-full rounded-2xl border border-white/10;
  max-width: 48rem;
  height: 80vh;
  background: #131313;
}
.fs-head {
  @apply flex items-center justify-between border-b border-white/10 px-5 py-3;
  h2 { @apply text-base font-bold text-white; margin: 0; }
}
.fs-head-right { @apply flex items-center gap-3; }
.fs-close {
  @apply text-white/50 hover:text-white bg-transparent border-none cursor-pointer text-base;
}
/* 全屏 chip 编辑器：滚动收在编辑区内，弹层/placeholder 挂在 wrap 上不被裁切 */
.fs-editor-wrap {
  position: relative;
  flex: 1;
  min-height: 0;
}
.fs-editor {
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  padding: 1rem 1.25rem;
  line-height: 1.6;
}
.fs-ph {
  pointer-events: none;
  position: absolute;
  top: 1rem; left: 1.25rem; right: 1.25rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.4);
}
.fs-footnote {
  @apply rounded-b-2xl border-t border-white/10 px-5 py-2.5 text-[11px] text-white/60;
  background: rgba(255, 255, 255, 0.04);
}
.prompt-head { @apply flex justify-between items-center mb-1; }
.prompt-head-actions { @apply flex items-center gap-1.5; }
.eg-btn {
  @apply inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] cursor-pointer;
  background: rgba(217, 70, 239, 0.15);
  border: 1px solid rgba(217, 70, 239, 0.4);
  color: rgba(255, 200, 255, 0.9);
  transition: all 0.15s;
}
.eg-btn:hover { background: rgba(217, 70, 239, 0.25); color: #fff; }
.eg-btn.active {
  background: rgba(217, 70, 239, 0.3);
  border-color: rgba(217, 70, 239, 0.7);
  color: #fff;
}
/* 参数区（Aspect/Duration/Resolution/Bitrate）：标题稍亮、选中实底/未选中淡底 */
.psec h3 { @apply text-sm font-medium text-white/90; }
.p-grid { @apply grid gap-2; }
.p-grid.cols-6 { grid-template-columns: repeat(6, minmax(0, 1fr)); }
.p-grid.cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.pbtn {
  @apply w-full rounded-md px-1 py-1.5 text-xs text-white/60 border border-transparent cursor-pointer transition;
  background: rgba(255, 255, 255, 0.05);
}
.pbtn:hover { @apply text-white; background: rgba(255, 255, 255, 0.1); }
.pbtn.active {
  @apply text-white font-medium;
  background: #3f3f46; /* 实底（不透明），区别于未选中的淡底 */
  border-color: rgba(255, 255, 255, 0.16);
}

/* Aspect ratio 按钮：上为按真实比例缩放的描边示意框，下为比例文字 */
.ratio-btn { @apply flex flex-col items-center gap-1.5 py-2 px-1; }
.ratio-frame { @apply flex items-center justify-center; width: 18px; height: 18px; }
.ratio-box {
  display: block;
  border: 1.5px solid currentColor;
  border-radius: 3px;
}
.ratio-label { @apply text-[10px] leading-none; }

/* Output 设置面板（PC 侧板 / 小屏弹窗） */
.out-mask {
  @apply fixed inset-0 flex items-center justify-center p-4;
  z-index: 90;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
}
.out-mask-side {
  background: transparent;
  backdrop-filter: none;
  padding: 0;
  display: block;
}
.out-dialog {
  @apply flex w-full flex-col rounded-2xl border border-white/10;
  max-width: 26rem;
  max-height: 85vh;
  background: #131316;
}
.out-dialog-side {
  position: fixed;
  width: 420px;
  max-width: calc(100vw - 24px);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}
.out-head {
  @apply flex items-center justify-between border-b border-white/10 px-5 py-3;
  h2 { @apply text-base font-bold text-white; margin: 0; }
}
.out-close {
  @apply text-white/50 hover:text-white bg-transparent border-none cursor-pointer text-base;
}
.out-body { @apply flex flex-col gap-4 overflow-y-auto px-5 py-4; }
.out-sec h3 { @apply mb-2; }
.p-grid.cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }

/* 素材放大预览 */
.preview-overlay {
  @apply fixed inset-0 flex items-center justify-center p-4;
  z-index: 110;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
}
.preview-close {
  @apply absolute rounded-full border-none cursor-pointer text-white/80;
  top: 1rem; right: 1rem;
  width: 36px; height: 36px;
  background: rgba(0, 0, 0, 0.6);
  font-size: 16px;
}
.preview-close:hover { background: #000; color: #fff; }
.preview-media {
  max-height: 85vh; max-width: 100%;
  border-radius: 0.75rem;
  object-fit: contain;
}
.preview-audio {
  @apply flex flex-col items-center gap-4 rounded-2xl border border-white/10 p-8 text-white/60;
  background: #131316;
  p { @apply m-0 max-w-[280px] truncate text-xs; }
}

.err {
  @apply rounded-md bg-red-500/10 border border-red-500/30 p-2 text-xs text-red-300;
}

/* Wrapper 让 popover 按编辑器内部光标/chip 锚点坐标定位 */
.textarea-wrap { position: relative; }

/* @ 素材引用选择弹层（JS 定位到光标/chip 锚点；.above = 翻转到锚点上方） */
.mention-popover {
  position: absolute;
  z-index: 40;
  width: 16rem;
  background: #181818;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.75rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
}
.mention-popover.above { transform: translateY(-100%); }
.mention-head {
  padding: 0.5rem 0.75rem;
  font-size: 10px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.5);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
/* 最多露出 7 个半选项（48px/项 × 7.5 = 360px），多出来的滚动查看 */
.mention-popover ul {
  list-style: none; margin: 0; padding: 0.25rem;
  max-height: 360px;
  overflow-y: auto;
}
.mention-popover li button {
  display: flex; align-items: center; gap: 0.5rem;
  width: 100%; text-align: left;
  padding: 6px 8px;
  background: transparent; border: none; border-radius: 0.35rem;
  cursor: pointer;
}
/* hover 与 ↑/↓ 键共用同一个高亮态（mouseenter 同步 activeIdx） */
.mention-popover li button.active { background: rgba(255, 255, 255, 0.08); }
.mention-popover .m-thumb {
  width: 36px; height: 36px;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  object-fit: cover;
}
.mention-popover .m-thumb-audio {
  display: flex; align-items: center; justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
}
/* token 用正常前景色（不再是主题色） */
.mention-popover .mt { flex: 1; font-size: 12px; color: white; font-family: monospace; }
/* 行尾类型图标 —— 快速区分图/视频/音频 */
.mention-popover .m-type { flex-shrink: 0; color: rgba(255, 255, 255, 0.5); }

/* Prompt length warning（仅超过 2500 时显示） */
.plen-bad { @apply text-red-300; }

/* Missing-reference 确认弹窗 */
.mr-overlay {
  position: fixed; inset: 0; z-index: 200;
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
}
.mr-dialog {
  width: 100%; max-width: 26rem;
  border-radius: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #131313;
  padding: 1.25rem;
  color: #fff;
}
.mr-dialog h3 { margin: 0 0 0.5rem; font-size: 1rem; font-weight: 700; }
.mr-dialog p { margin: 0 0 1.25rem; font-size: 0.8rem; line-height: 1.5; color: rgba(255, 255, 255, 0.6); }
.mr-actions { display: flex; justify-content: flex-end; gap: 0.5rem; }
.mr-btn {
  padding: 0.5rem 1rem;
  border: none; border-radius: 0.5rem;
  font-size: 0.8rem; font-weight: 600;
  cursor: pointer;
}
.mr-btn.ghost {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.12);
}
.mr-btn.ghost:hover { background: rgba(255, 255, 255, 0.12); color: #fff; }
.mr-btn.primary {
  background: linear-gradient(90deg, #d946ef, #f43f5e);
  color: #fff;
}
.mr-btn.primary:hover { opacity: 0.92; }
</style>
